'use strict';

import _ from 'lodash';
import omniclone from 'omniclone';
import getHierarchy from '~/shared/src/utilities/hierarchyBuilder';
import metaModel from '~/shared/src/utilities/metaModel';
import {ExtendTreeNode} from '~/shared/src/utilities/modules/tree';
import Item, {
    ItemAttributes,
    ItemCollection,
    ItemOnlyAttributesSettable
} from '~/shared/src/ci/Item';
import Reference, {ReferenceAttributes} from '~/shared/src/ci/Reference';
import {
    Action,
    DataObjects
} from '~/shared/src/utilities/action';
import {ElusiveData} from '~/frontend/src/actions/elusiveData';
import dataExtraction, {
    ExtractedProperties
} from '~/shared/src/utilities/dataExtraction';
import {getIconCssClassName} from '~/shared/src/utilities/getCssClassName';
import {__} from '~/shared/src/utilities/messageCatalog';
import md5 from 'md5';
import ReferenceSet from '~/shared/src/mm/ReferenceSet';
import dataUtils, {
    ItemData,
    ObjectData,
    PropertyData,
    ReferenceData
} from '~/shared/src/utilities/dataUtils';
import {
    buildDataDescriptor,
    DataDescriptor,
    DataStructure,
    getDataObject,
    ItemDataDescriptor,
    ObjectDataDescriptorParams,
    PropertyDataDescriptor
} from '~/shared/src/utilities/dataStructure';
import {
    checkCardinalityFeOnly,
    resolveConditionRecursive
} from '~/frontend/src/utilities/dataUtils';
import {Condition} from '~/shared/src/utilities/condition';
import {DropEffect} from './keyboardUtils';
import messageUtils from '~/shared/src/utilities/messageUtils';
import {Style} from '~/shared/src/utilities/style';
import {CSSProperties} from 'react';
import {store} from '~/frontend/src/store';
import PropertyResolver from '~/shared/src/utilities/PropertyResolver';
import ReferencePropertyResolver from '~/shared/src/utilities/ReferencePropertyResolver';
import {
    ClonedObjectCache,
    ClonedObjectsIdMappings,
    ResolvedDataObject,
    ResolvedDataObjects,
    ResolvedDataObjectState
} from '~/frontend/src/utilities/ActionMixin';
import {Function} from '~/shared/src/utilities/function';
import {getDataStructure} from '~/shared/src/utilities/modules/finder';
import User from '~/shared/src/model/User';
import {performanceLog} from '~/shared/src/utilities/log';
import {resolveLigatureIcon} from '~/frontend/src/utilities/renderHelpers';
import {ProcessResultPart} from '~/shared/src/utilities/modules/cypherParser';


/**
 * @type NodeMap
 * A node id to node map
 */
export type NodeMap = { [ id : string ] : ExtendTreeNode };

export type ReferenceSetsWithDirection = {
    [ referenceSetId : number ] : 'src' | 'dest'
};

/**
 * @type PayloadData
 */
type PayloadData = {
    dataObjectNode : ExtendTreeNode;
    children? : PayloadData[],
    ciItem : Item;
    referenceId : number;
    cypherMatchNum : number;
    payloadIdPath : string;
    referenceDirection : string;
    isRecursive : boolean;
    stopped? : boolean;
};

/**
 * @type ReferenceDescription
 * ReferenceAttributes with referenceTypeName and index
 */
export type ReferenceDescription = ReferenceAttributes & {
    referenceTypeName? : string,
    direction? : string,
    idx : number
};

export type NodePermissions = {
    disableAddExistingItem : boolean,
    disableAddNewItem : boolean,
    disableDeleteItem : boolean,
    disableDeleteReference : boolean,
    disableDeleteDescendantItems : boolean,
    disableDeleteDescendantReferences : boolean,
    allowClone? : boolean
};

/**
 * Utility class to build dynamic trees
 */
export default class TreeUtils {

    /**
     * Grab all relevant data from elusiveData object in redux store
     * @param dataContext
     * @param metaContext
     * @param elusiveData
     */
    static getConfigFromElusiveData( dataContext : string, metaContext : string, elusiveData : ElusiveData ) : {
        staticTree : any, dataObjectTree : any, dataStructure : any,
        dataObjectMapping : any, payloadSchema : Schema, isStopped : boolean, metaItems : ItemCollection, items : ItemCollection,
        cypherConfig : any, payloadCypherConfig : any
    } {
        const retVal = {
            staticTree         : elusiveData?.metaData?.treeData,
            dataObjectTree     : elusiveData?.metaData?.dataObjectTree,
            dataStructure      : elusiveData?.metaData?.dataStructure,
            dataObjectMapping  : elusiveData?.metaData?.dataObjectMapping,
            payloadSchema      : elusiveData?.metaData?.payloadSchema,
            isStopped          : elusiveData?.metaData?.isStopped,
            metaItems          : {},
            items              : {},
            cypherConfig       : elusiveData?.metaData?.cypherConfig,
            payloadCypherConfig: elusiveData?.data?.cypherConfig
        };
        if ( global.data[ dataContext ] ) {
            retVal.items = elusiveData?.data?.items ? Object.keys( elusiveData.data.items ).reduce(
                ( cumulator : ItemCollection, itemId : number ) => {
                    cumulator[ Number( itemId ) ] = global.data[ dataContext ]?.items?.[ Number( itemId ) ];
                    return cumulator;
                }, {} ) : {};
        }
        if ( global.data[ metaContext ] ) {
            retVal.metaItems = elusiveData?.metaData?.items ? Object.keys( elusiveData.metaData.items ).reduce(
                ( cumulator : ItemCollection, itemId : number ) => {
                    cumulator[ Number( itemId ) ] = global.data[ metaContext ]?.items?.[ Number( itemId ) ];
                    return cumulator;
                }, {} ) : {};
        }
        return retVal;
    }

    /**
     * the (static) tree of TreeNodes and DynamicNodes (tree structure) as delivered by backend
     * @type {TreeData}
     */
    staticTree? : TreeData;

    /**
     * the tree of DataObjects (tree payload) as delivered by backend
     * @type {TreeData}
     */
    dataObjectTree : TreeData;

    /**
     * structure of DataObjects as delivered by backend
     * @type {TreeData}
     */
    dataStructure : DataStructure;

    /**
     * the mapping between DynamicNodes (even generated) and DataObjects
     * @type {{ [ dynamicNodeId : string ] : string[] }}
     */
    dataObjectMapping : { [ dynamicNodeId : string ] : string[] };

    /**
     * the CFG ciData describing the tree
     * @type {Object}
     */
    metaItems? : ItemCollection;

    /**
     * the cypherConfig describing the tree
     * @type {Object}
     */
    cypherConfig : ProcessResultPart[];

    /**
     * the cypherConfig describing the payload data relations
     * @type {Object}
     */
    payloadCypherConfig : ProcessResultPart[];

    dataContext : string;
    metaContext : string;

    /**
     * item id of the payload root item
     * @type {number}
     */
    payloadRootItemId? : number;

    /**
     * the payload ciData describing the content
     * @type {Object}
     */
    payloadItems? : ItemCollection;

    user? : User;

    /**
     * cache for payloadCollections (performance)
     */
    payloadCollectionCache? : any;

    /**
     * Flat node id to node map of static nodes as delivered by backend
     * @type {Object}
     */
    flatStaticNodeMap? : NodeMap;

    /**
     * Flat node id to node map
     * @type {Object}
     */
    flatNodeMap? : NodeMap;
    flatDataObjectNodeMap? : NodeMap;

    dataObjectProvidesDataForDynamicNodeCirsId : number;
    conditionIsFilterForDynamicNodeCirsId : number;

    /**
     * Flat node id to sibling nodes map. Sibling means, the node derives from same DataObject
     * @type {Object}
     */
    siblingNodeMap? : { [ id : string ] : ExtendTreeNode[] };

    /**
     * The payload schema
     * @type {string}
     */
    payloadSchema : Schema = 'DATA';

    payloadIdToDataObjectNodeIdMap : { [ payloadId : number ] : string[] } = {};
    payloadIdToDynamicNodeIdMap : { [ payloadId : number ] : string[] } = {};
    payloadReferenceIdToDynamicNodeIdMap : { [ payloadReferenceId : number ] : string[] } = {};

    filteredItemAttributesAndProperties : {
        [ itemTypeId : number ] : {
            attributes : ( keyof ItemOnlyAttributesSettable )[];
            propertyTypeIds : number[];
        }
    } = {};

    sortingItemAttributesAndProperties : {
        [ itemTypeId : number ] : {
            attributes : ( keyof ItemOnlyAttributesSettable )[];
            propertyTypeIds : number[];
        }
    } = {};

    lastTreeNodeData? : TreeNodeData[];
    lastVersionCount? : { forFiltering : number, forSorting : number };
    lastReferenceIds? : number[];
    lastLanguage? : string;

    isStopped : boolean;

    /**
     * Collect relevant references between DataObjects and TreeNodes (the left side of ViewDesigner)
     */
    dataObjectToTreeNodeReferenceSetIds : { [ itemTypeName : string ] : number[] };

    panelConfigBelongsToTreeNodeCirsId : number;
    isStyleForCirsIds : number[];
    isIconForCirsIds : number[];
    isApplyingCirsIds : number[];
    isTriggerForCirsIds : number[];
    dataObjectIsReferencedToDataObjectCirsId : number;

    isDisablingAddExistingItemForDynamicNodeReferenceSetId : number;
    isDisablingAddNewItemForDynamicNodeReferenceSetId : number;
    isDisablingDeleteItemForDynamicNodeReferenceSetId : number;
    isDisablingDeleteReferenceForDynamicNodeReferenceSetId : number;
    providesDataForLeftExpressionReferenceSetId : number;
    providesDataForRightExpressionReferenceSetId : number;

    nodeStyleCache : { [ nodeId : string ] : { style : CSSProperties, className : string } } = {};
    nodeIconCache : { [ nodeId : string ] : string | ( () => React.ReactNode ) | undefined } = {};

    relatedDynamicNodeRefsCache : { [ dataObjectId : number ] : Reference[] | undefined } = {}
    parentPayloadIdPathsCache : { [ nodeId : string ] : string[] } = {}

    debug : boolean = false;

    functions? : Function[];

    constructor( uid : string, dataContext : string, elusiveData? : ElusiveData, flatNodeMap? : NodeMap, flatDataObjectNodeMap? : NodeMap, user? : User, debug : boolean = false ) {

        this.debug = this.debug || debug;
        const startTime = this.debug ? performance.now() : 0;

        this.dataContext = dataContext;
        this.metaContext = global.data[ dataContext + '-meta' ] ? dataContext + '-meta' : uid + '-meta';

        this.user = user;

        // flatNodeMap and flatDataObjectNodeMap are provided from corresponding ItemTree (caching for tab changes)

        // take flatNodeMap from constructor, if provided
        if ( flatNodeMap && elusiveData?.metaData?.treeData ) {
            this.flatNodeMap = flatNodeMap;
            this.flatStaticNodeMap = this.getFlatNodeMap( elusiveData?.metaData?.treeData );
        }
        // otherwise from metaData, if provided
        else if ( elusiveData?.metaData?.treeData ) {
            this.flatStaticNodeMap = this.flatNodeMap = this.getFlatNodeMap( elusiveData.metaData.treeData );
        }
        // if elusive data contains no metadata, build flat node map direct from payload
        else if ( elusiveData?.data?.treeData?.[ 0 ] ) {
            this.flatStaticNodeMap = this.flatNodeMap = this.getFlatNodeMap( elusiveData.data.treeData[ 0 ] );
        }

        if ( flatDataObjectNodeMap ) {
            this.flatDataObjectNodeMap = flatDataObjectNodeMap;
        }

        let dataObjectTree;
        let dataStructure;
        let dataObjectMapping : { [ dynamicNodeId : string ] : string[] } = {};
        let payloadSchema;
        let isStopped,
            metaItems,
            cypherConfig,
            payloadCypherConfig;

        if ( elusiveData ) {
            ( {
                staticTree: this.staticTree,
                dataObjectTree,
                dataStructure,
                dataObjectMapping,
                payloadSchema,
                metaItems,
                cypherConfig,
                payloadCypherConfig,
                isStopped
            } = TreeUtils.getConfigFromElusiveData( dataContext, this.metaContext, elusiveData ) );
        }

        if ( this.staticTree?.[ 0 ]?.data.itemId ) {
            const treeRootId   = this.staticTree[ 0 ].data.itemId,
                  treeRootItem = global.data[ this.metaContext ]?.items[ treeRootId ];

            if ( treeRootItem ) {
                this.functions = dataExtraction.extractFunctions( treeRootItem, this.metaContext );
            }
        }

        this.dataObjectTree = dataObjectTree;
        this.dataStructure = dataStructure;
        this.dataObjectMapping = dataObjectMapping;
        this.metaItems = metaItems;

        this.cypherConfig = cypherConfig;
        this.payloadSchema = payloadSchema ?? 'DATA';
        this.payloadCypherConfig = payloadCypherConfig;
        this.isStopped = !!isStopped;

        // get metamodel ids
        this.dataObjectProvidesDataForDynamicNodeCirsId = metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'DataObject', 'DynamicNode', 'providesDataFor', 'src' )?.[ 0 ]?.id;
        this.conditionIsFilterForDynamicNodeCirsId = metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'Condition', 'DynamicNode', 'isFilterFor', 'src' )?.[ 0 ]?.id;
        this.panelConfigBelongsToTreeNodeCirsId = metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'PanelConfig', 'TreeNode', 'belongsTo', 'src' )?.[ 0 ]?.id;
        this.isStyleForCirsIds = [
            metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'Style', 'TreeNode', 'isStyleFor', 'src' )?.[ 0 ]?.id,
            metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'Style', 'DynamicNode', 'isStyleFor', 'src' )?.[ 0 ]?.id
        ];
        this.isIconForCirsIds = [
            metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'Image', 'TreeNode', 'isIconFor', 'src' )?.[ 0 ]?.id,
            metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'Image', 'DynamicNode', 'isIconFor', 'src' )?.[ 0 ]?.id
        ];
        this.isApplyingCirsIds = [
            metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'Condition', 'Style', 'isApplying', 'src' )?.[ 0 ]?.id,
            metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'Condition', 'Image', 'isApplying', 'src' )?.[ 0 ]?.id
        ];
        const dataObjectCitId = metaModel.CFG.getItemType( 'DataObject' )?.id ?? 0;
        const relevantReferenceTypeIds : number[] = [
            metaModel.CFG.getReferenceType( 'providesDataFor' )?.id ?? 0,
            metaModel.CFG.getReferenceType( 'isUsedBy' )?.id ?? 0,
            metaModel.CFG.getReferenceType( 'providesDataForLeftExpression' )?.id ?? 0,
            metaModel.CFG.getReferenceType( 'providesDataForRightExpression' )?.id ?? 0
        ];
        this.isTriggerForCirsIds = [
            metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'DynamicNode', 'Action', 'isTriggerFor', 'src' )?.[ 0 ]?.id,
            metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'TreeNode', 'Action', 'isTriggerFor', 'src' )?.[ 0 ]?.id
        ];
        this.isDisablingAddExistingItemForDynamicNodeReferenceSetId = metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'Condition', 'DynamicNode', 'isDisablingAddExistingItemFor', 'src' )?.[ 0 ]?.id;
        this.isDisablingAddNewItemForDynamicNodeReferenceSetId = metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'Condition', 'DynamicNode', 'isDisablingAddNewItemFor', 'src' )?.[ 0 ]?.id;
        this.isDisablingDeleteItemForDynamicNodeReferenceSetId = metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'Condition', 'DynamicNode', 'isDisablingDeleteItemFor', 'src' )?.[ 0 ]?.id;
        this.isDisablingDeleteReferenceForDynamicNodeReferenceSetId = metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'Condition', 'DynamicNode', 'isDisablingDeleteReferenceFor', 'src' )?.[ 0 ]?.id;
        this.providesDataForLeftExpressionReferenceSetId = metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'DataObject', 'Condition', 'providesDataForLeftExpression', 'src' )?.[ 0 ]?.id;
        this.providesDataForRightExpressionReferenceSetId = metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'DataObject', 'Condition', 'providesDataForRightExpression', 'src' )?.[ 0 ]?.id;
        this.dataObjectIsReferencedToDataObjectCirsId = metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'DataObject', 'DataObject', 'isReferencedTo', 'src' )?.[ 0 ]?.id;

        // Collect relevant references between DataObjects and TreeNodes (the left side of ViewDesigner)
        this.dataObjectToTreeNodeReferenceSetIds = {};

        const defaultRefSets = metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'DataObject', '*', '*', 'src' ).filter( rset => rset.referenceType.name !== 'isRootOf' ).map( rset => rset.id );

        for ( const cypherConf of this.payloadCypherConfig ?? [] ) {
            for ( const recursionConfig of cypherConf.recursionConfigs ?? [] ) {
                const computedIds       = recursionConfig.reference?.computedIds,
                      computedRefSetIds = computedIds?.referenceSetIds.length
                          ? computedIds?.referenceSetIds
                          : defaultRefSets;

                if ( computedIds?.outItemTypeIds.includes( dataObjectCitId ) && ( !computedIds?.referenceTypeIds.length || _.intersection( computedIds?.referenceTypeIds, relevantReferenceTypeIds ).length ) ) {
                    for ( const cirsId of computedRefSetIds ) {
                        const rset = metaModel.CFG.getReferenceSet( cirsId );

                        if ( rset && rset.referenceType.name !== 'isRootOf' ) {
                            this.dataObjectToTreeNodeReferenceSetIds[ rset.destItemType.name ] = this.dataObjectToTreeNodeReferenceSetIds[ rset.destItemType.name ] || [];
                            if ( !this.dataObjectToTreeNodeReferenceSetIds[ rset.destItemType.name ].includes( cirsId ) ) {
                                this.dataObjectToTreeNodeReferenceSetIds[ rset.destItemType.name ].push( cirsId );
                            }
                        }
                    }
                }
            }
        }

        this.getFilterConditions();
        this.collectAttributesAndPropertiesUsedByOrder();

        if ( this.debug ) {
            performanceLog( 'TreeUtils - constructor', startTime );
        }
    }

    setData( payloadRootItemId : number, treeData : TreeNodeData[], payloadIdToDynamicNodeIdMap : { [ p : number ] : string[] } = {} ) {
        this.payloadRootItemId = payloadRootItemId;
        this.lastTreeNodeData = treeData;
        this.payloadIdToDynamicNodeIdMap = payloadIdToDynamicNodeIdMap;
        this.payloadItems = global.data[ this.dataContext ]?.items ?? {};
    }

    /**
     * Apply payload data to DataObject tree and merge DataObject tree with (static) TreeNode tree
     * @param {number} payloadRootItemId id of the payload root item
     * @param {{[p: string]: boolean}} expandedKeys map of expanded node keys
     * @param {number} newPayloadReferenceId id of the newly added payload reference. Used to expand path to it.
     * @return {ItemCollection}
     */
    applyData( payloadRootItemId : number, expandedKeys? : {
        [ p : string ] : boolean
    }, newPayloadReferenceId? : number ) : TreeNodeData[] {
        const startTime    = this.debug ? performance.now() : 0,
              refs         = global.data[ this.dataContext ]?.references,
              referenceIds = refs ? Object.keys( refs ) : [],
              elusiveData  = store.getState().elusiveData?.[ this.dataContext ];

        let retVal : TreeNodeData[],
            relevantVersionCount : { forFiltering : number, forSorting : number } = { forFiltering: 0, forSorting: 0 };

        if ( elusiveData ) {
            // check for any relevant changes
            relevantVersionCount = this.getRelevantVersionCount( elusiveData )
        }

        this.payloadItems = global.data[ this.dataContext ]?.items ?? {};

        if ( !this.lastTreeNodeData || payloadRootItemId !== this.payloadRootItemId || relevantVersionCount.forFiltering !== this.lastVersionCount?.forFiltering || !_.isEqual( Object.keys( this.payloadItems ), Object.keys( this.payloadItems ) ) || !_.isEqual( referenceIds, this.lastReferenceIds ) ) {
            if ( this.debug ) {
                console.log( 'rebuild' );
            }
            retVal = omniclone( this.staticTree ?? [ { children: [] } ] );

            if ( this.dataObjectTree ) {
                const dataObjectTree : any = omniclone( this.dataObjectTree );
                this.payloadIdToDataObjectNodeIdMap = {};
                this.payloadRootItemId = payloadRootItemId;

                this.nodeIconCache = {};

                this.payloadCollectionCache = {};
                this.payloadIdToDynamicNodeIdMap = {};
                this.payloadReferenceIdToDynamicNodeIdMap = {};

                this.flatNodeMap = this.flatDataObjectNodeMap = Object.assign( this.getFlatNodeMap( dataObjectTree ), this.flatStaticNodeMap );
                this.siblingNodeMap = {};
                dataObjectTree.data.payloadId = payloadRootItemId;
                dataObjectTree.data.payloadIdPath = '-' + payloadRootItemId;

                let startTime2 = this.debug ? performance.now() : 0;
                // apply payloadData to DataObject tree
                this.applyDynamicNodes( dataObjectTree, undefined, this.payloadCypherConfig );
                if ( this.debug ) {
                    performanceLog( 'TreeUtils - applyDynamicNodes', startTime2 );
                }

                this.relatedDynamicNodeRefsCache = {};
                this.parentPayloadIdPathsCache = {};
                startTime2 = this.debug ? performance.now() : 0;
                this.mergeStaticTreeAndDataObject( retVal[ 0 ] as ExtendTreeNode );
                if ( this.debug ) {
                    performanceLog( 'TreeUtils - mergeStaticTreeAndDataObject', startTime2 );
                }

                startTime2 = this.debug ? performance.now() : 0;
                this.flatNodeMap = this.getFlatNodeMap( retVal[ 0 ] as ExtendTreeNode );
                if ( this.debug ) {
                    performanceLog( 'TreeUtils - getFlatNodeMap', startTime2 );
                }

            }
            this.lastTreeNodeData = retVal;
        }
        else {
            if ( this.debug ) {
                console.log( 'cache' );
            }

            if ( this.flatNodeMap ) {
                for ( const node of Object.values( this.flatNodeMap ) ) {
                    if ( relevantVersionCount.forSorting !== this.lastVersionCount.forSorting ) {
                        this.resortChildNodes( node );
                    }

                    if ( node.data.payloadId ) {
                        this.decoratePayloadNode( node );
                    }
                    else {
                        this.decorateTreeNode( node );
                    }
                }
            }

            retVal = [ ...this.lastTreeNodeData ];
            if ( retVal[ 0 ].children ) {
                retVal[ 0 ].children = [ ...retVal[ 0 ].children ];
            }
        }

        this.lastVersionCount = relevantVersionCount;

        this.lastReferenceIds = referenceIds;
        this.lastLanguage = this.user?.language;

        if ( expandedKeys ) {
            // apply openExpanded node keys to expandedKeys map, if the expand state is untouched there
            _.forOwn( this.flatNodeMap!, node => {
                if ( node.openExpanded && expandedKeys[ node.key! ] === undefined ) {
                    expandedKeys[ node.key! ] = true;
                }
            } );
            // expand path to new node if given
            if ( newPayloadReferenceId ) {
                const newNodeIds = this.getDynamicNodeIdByPayloadReferenceId( newPayloadReferenceId );

                for ( const nodeId of newNodeIds ) {
                    const node = this.getNodeById( nodeId );

                    let parentNode : ExtendTreeNode | undefined = node?.data.parentNodeId
                        ? this.getNodeById( node.data.parentNodeId )
                        : undefined;

                    while ( parentNode ) {
                        expandedKeys[ parentNode.key! ] = true;
                        parentNode = parentNode.data.parentNodeId
                            ? this.getNodeById( parentNode.data.parentNodeId )
                            : undefined;
                    }
                }
            }
        }

        // remove leaf nodes with visibility set to hiddenIfLeave
        for ( const node of Object.values( this.flatNodeMap ?? {} ) ) {
            if ( node.leaf && node.visibility === 'hiddenIfLeave' ) {
                const parentNode = this.getNodeById( node.data.parentNodeId );

                if ( parentNode?.children ) {
                    parentNode.children = parentNode?.children.filter( cn => cn.id !== node.id );
                    parentNode.leaf = !parentNode.children?.length;
                }

                delete this.flatNodeMap![ node.id ];
            }
        }

        this.applyDirty();

        if ( this.debug ) {
            performanceLog( 'TreeUtils - applyData', startTime );
        }

        return retVal[ 0 ].children!;
    }

    applyDirty() {
        // mark path to invalid nodes
        const flatNodes = Object.values( this.flatNodeMap ?? {} );

        for ( const node of flatNodes ) {
            if ( node ) {
                this.markNodeClean( node );
            }
        }
        for ( const node of flatNodes ) {
            if ( node.data?.isDirty ) {
                this.markPathDirty( node );
            }
        }
    }

    applyErrorData( nodeIdToErrorDataMap? : { [ nodeId : string ] : ErrorData | undefined } ) {
        // mark path to invalid nodes
        if ( this.flatNodeMap ) {
            const invalidNodeIds : { [ nodeId : string ] : true } = {};

            for ( const node of Object.values( this.flatNodeMap ) ) {
                this.markPathValidOrInvalid( node, invalidNodeIds, nodeIdToErrorDataMap?.[ node.id ] );
            }
        }
    }

    /**
     * Count changes of items and references with impact on filtering and sorting
     * @param elusiveData
     */
    getRelevantVersionCount( elusiveData : ElusiveData ) : { forFiltering : number, forSorting : number } {
        const retVal = { forFiltering: 0, forSorting: 0 };
        const objectTypes : [ 'items', 'references' ] = [ 'items', 'references' ];
        const collections = {
            forFiltering: this.filteredItemAttributesAndProperties,
            forSorting  : this.sortingItemAttributesAndProperties
        }
        for ( const objectType of objectTypes ) {
            for ( const itemId of Object.keys( elusiveData.data?.[ objectType ] ?? {} ) ) {
                const obj = global.data[ this.dataContext ]?.[ objectType ][ itemId ];

                if ( obj ) {
                    for ( const collectionName of Object.keys( collections ) ) {
                        const collection = collections[ collectionName ];
                        let isRelevant = collectionName === 'forFiltering' && obj instanceof Reference;
                        if ( obj instanceof Item ) {
                            let relevantAttrsAndProps = collection[ obj.itemTypeId ];
                            if ( collection[ 0 ] ) {
                                // merge
                                if ( relevantAttrsAndProps ) {
                                    relevantAttrsAndProps.attributes = relevantAttrsAndProps.attributes.concat( collection[ 0 ].attributes );
                                    relevantAttrsAndProps.propertyTypeIds = relevantAttrsAndProps.propertyTypeIds.concat( collection[ 0 ].propertyTypeIds );
                                }
                                else {
                                    relevantAttrsAndProps = collection[ 0 ];
                                }
                            }
                            if ( relevantAttrsAndProps ) {
                                // check if relevant attributes has been changed
                                isRelevant = relevantAttrsAndProps.attributes.some( attrName => obj.attributesModified.has( attrName ) );
                                isRelevant = isRelevant || relevantAttrsAndProps.propertyTypeIds.some( propertTypeId => !!obj.properties.get( propertTypeId )?.isNew() || !!obj.properties.get( propertTypeId )?.isModified() );
                            }
                        }
                        if ( isRelevant ) {
                            retVal[ collectionName ] += elusiveData.data![ objectType ]![ itemId ];
                        }
                    }
                }
            }
        }
        return retVal;
    }

    getFilterConditions() {
        const dataDescriptors : DataDescriptor[] = [];

        const getConditionDataDescriptorRecursive = ( condition : Condition, defaultDataStrucure : DataStructure ) => {
            for ( const side of [ 'left', 'right' ] ) {
                const sideExpression               = `${side}Expression` as 'leftExpression' | 'rightExpression',
                      sideExpressionDataDescriptor = `${side}ExpressionDataDescriptor` as 'leftExpressionDataDescriptor' | 'rightExpressionDataDescriptor';

                if ( !condition[ sideExpressionDataDescriptor ] && _.isString( condition[ sideExpression ] ) ) {
                    condition[ sideExpressionDataDescriptor ] = buildDataDescriptor( { attributePath: condition[ sideExpression ] as string }, defaultDataStrucure )
                }
                if ( condition[ sideExpressionDataDescriptor ]?.alias ) {
                    if ( !dataDescriptors.map( dD => dD.alias ).includes( condition[ sideExpressionDataDescriptor ]!.alias ) ) {
                        dataDescriptors.push( condition[ sideExpressionDataDescriptor ]! );
                    }
                }

                if ( Array.isArray( condition[ sideExpression ] ) ) {
                    for ( const subCondition of condition[ sideExpression ] ?? [] ) {
                        if ( !_.isString( subCondition ) ) {
                            getConditionDataDescriptorRecursive( subCondition as Condition, defaultDataStrucure );
                        }
                    }
                }
            }
        };

        for ( const itemId in this.metaItems! ) {
            const item = this.metaItems[ itemId ];

            if ( item?.itemType.name === 'Condition' ) {
                const dynamicNode = dataUtils.findReferences( this.metaContext, this.conditionIsFilterForDynamicNodeCirsId, item.id )?.[ 0 ]?.destItem;

                let condition : Condition | undefined;

                if ( dynamicNode && ( condition = dataExtraction.extractConditionData( item, this.metaContext, this.dataStructure ) ) ) {
                    const dataObject    = dataUtils.findReferences( this.metaContext, this.dataObjectProvidesDataForDynamicNodeCirsId, undefined, dynamicNode.id )?.[ 0 ]?.srcItem,
                          itemTypeName  = dataObject
                              ? dataExtraction.extractPropertiesOfItem( dataObject )?.objectType
                              : undefined,
                          dataStructure = getDataStructure( this.payloadSchema, itemTypeName, this.user! );

                    getConditionDataDescriptorRecursive( condition, dataStructure ?? this.dataStructure );
                }
            }
        }

        for ( const dataDescriptor of dataDescriptors ) {
            const itemTypeId = dataDescriptor.dataObject?.item.objectType ?? '*' !== '*'
                ? metaModel[ this.payloadSchema ].getItemType( dataDescriptor.dataObject!.item.objectType! )?.id ?? 0
                : 0;

            this.filteredItemAttributesAndProperties[ itemTypeId ] = this.filteredItemAttributesAndProperties[ itemTypeId ] ?? {
                attributes     : [],
                propertyTypeIds: []
            }

            if ( dataDescriptor.type === 'item' && dataDescriptor.attribute && !this.filteredItemAttributesAndProperties[ itemTypeId ].attributes.includes( dataDescriptor.attribute as keyof ItemOnlyAttributesSettable ) ) {
                this.filteredItemAttributesAndProperties[ itemTypeId ].attributes.push( dataDescriptor.attribute as keyof ItemOnlyAttributesSettable );
            }
            else if ( dataDescriptor.type === 'property' ) {
                const propertTypeId = metaModel[ this.payloadSchema ].getPropertyType( dataDescriptor.propertyTypeName )?.id;

                if ( propertTypeId && !this.filteredItemAttributesAndProperties[ itemTypeId ].propertyTypeIds.includes( propertTypeId ) ) {
                    this.filteredItemAttributesAndProperties[ itemTypeId ].propertyTypeIds.push( propertTypeId );
                }
            }
        }
    }

    /**
     * Collect all item attributes and propeties used in orderBy properties.
     * This is used to decide if tree must be re-build/reordered
     */
    collectAttributesAndPropertiesUsedByOrder() : void {

        const regex = /((?:property\.|item\.)[a-zA-Z]+)/gm;

        for ( const itemId in this.metaItems! ) {
            const item = dataExtraction.extractPropertiesOfItem( this.metaItems[ itemId ] );

            if ( item.orderBy ) {
                const dataObject = dataUtils.findReferences( this.metaContext, this.dataObjectProvidesDataForDynamicNodeCirsId, undefined, item.id )?.[ 0 ]?.srcItem;
                const objectType = dataObject ? dataExtraction.extractPropertiesOfItem( dataObject )?.objectType : '*';

                const itemTypeId = objectType ?? '*' !== '*'
                    ? metaModel[ this.payloadSchema ].getItemType( objectType! )?.id ?? 0
                    : 0;
                this.sortingItemAttributesAndProperties[ itemTypeId ] = this.sortingItemAttributesAndProperties[ itemTypeId ] ?? {
                    attributes     : [],
                    propertyTypeIds: []
                }

                let match : RegExpExecArray | null;
                while ( ( match = regex.exec( item.orderBy ) ) !== null ) {
                    const filterAttribute = match[ 1 ].split( '.' );
                    if ( filterAttribute.length === 2 ) {
                        // item attribute
                        if ( filterAttribute[ 0 ] === 'item' ) {
                            this.sortingItemAttributesAndProperties[ itemTypeId ].attributes.push( filterAttribute[ 1 ] as keyof ItemOnlyAttributesSettable );
                        }
                        // property
                        else {
                            const propertTypeId = metaModel[ this.payloadSchema ].getPropertyType( filterAttribute[ 1 ] )?.id;
                            if ( propertTypeId && !this.sortingItemAttributesAndProperties[ itemTypeId ].propertyTypeIds.includes( propertTypeId ) ) {
                                this.sortingItemAttributesAndProperties[ itemTypeId ].propertyTypeIds.push( propertTypeId );
                            }
                        }
                    }
                }
            }
        }
    }

    /**
     * Resort child nodes given node
     * @param node
     */
    resortChildNodes( node : ExtendTreeNode ) {
        if ( node.children ) {
            let newChildren : ExtendTreeNode[] = [];
            let childrenToBeOrdered : ExtendTreeNode[] = [];
            let orderBy : string = 'item.key';
            for ( const child of node.children ) {
                // do not order static nodes
                if ( !child.data.payloadId || ( orderBy && child.data.ciItem.orderBy !== orderBy ) ) {
                    if ( childrenToBeOrdered.length ) {
                        childrenToBeOrdered.sort( this.payloadNodeSorter.bind( this, orderBy ) );
                        newChildren = newChildren.concat( childrenToBeOrdered );
                        childrenToBeOrdered = [];
                    }
                }

                if ( child.data.payloadId && child.data.ciItem.orderBy ) {
                    childrenToBeOrdered.push( child );
                    orderBy = child.data.ciItem.orderBy;
                }
                else {
                    newChildren.push( child );
                }
            }

            if ( childrenToBeOrdered.length ) {
                childrenToBeOrdered.sort( this.payloadNodeSorter.bind( this, orderBy ) );
                newChildren = newChildren.concat( childrenToBeOrdered );
            }

            node.children = newChildren;
        }
    }

    getFunctions() : Function[] | undefined {
        return this.functions;
    }

    /**
     * Get ids of all items represented by a tree node (DynamicNode)
     */
    getPayloadIdsOfNodes() : number[] {
        let retVal : number[] = [];

        // if payloadIdToDynamicNodeIdMap is not set, use data from FlatNodeMap (e.g. after switching tab panels)
        if ( !Object.keys( this.payloadIdToDynamicNodeIdMap ).length && this.flatNodeMap ) {
            for ( const node of Object.values( this.flatNodeMap ) ) {
                if ( node.data?.payloadId && !retVal.includes( node.data.payloadId ) ) {
                    retVal.push( node.data.payloadId );
                }
            }
        }
        else {
            retVal = Object.keys( this.payloadIdToDynamicNodeIdMap ).map( Number );
        }

        return retVal;
    }

    /**
     * Check if dropping dragNode to dropNode is allowed
     * @param {TreeNode} dragNode the dragged node (check if it can be dropped to dropNode)
     * @param {TreeNode} dropNode the (potentially) drop target
     * @param {TreeDropEffect} dropEffect
     * @return {false|{srcId: Number, destId: Number, referenceSetId: Number, referenceTypeId: Number}} returns false if not, a reference config object if yes
     */
    checkDropTarget( dragNode : ExtendTreeNode, dropNode : ExtendTreeNode, dropEffect : DropEffect = 'move' ) : ReferenceDescription[] | false {
        const retVal : ReferenceDescription[] = [];

        // get payload items of affected nodes
        const dragItem = this.payloadItems?.[ dragNode.data.payloadId ];
        const dropItem = this.payloadItems?.[ dropNode.data.payloadId || dropNode.data.delegatedPayloadId ];

        if ( dragItem && dropItem ) {

            // search for possible reference sets between this 2 items according to the metaModel
            let possibleReferenceSets = metaModel[ this.payloadSchema ].getReferenceSetsForItemTypes( dragItem.itemTypeId, dropItem.itemTypeId );
            const allowedReferenceSets = ( Object.keys( dropNode.data.allowedPayloadCirsIds ?? [] ) as string[] ).map( ( cirsId : string ) => metaModel[ this.payloadSchema ].getReferenceSet( Number( cirsId ) ) ?? {} as ReferenceSet );

            if ( possibleReferenceSets && ( possibleReferenceSets = _.intersection( possibleReferenceSets, allowedReferenceSets ) ).length ) {
                for ( const referenceSet of possibleReferenceSets ) {
                    const direction = dragItem.itemTypeId === referenceSet.srcItemTypeId ? 'SrcToDest' : 'DestToSrc';

                    if (
                        ( direction === 'SrcToDest' && dropNode.data.allowedPayloadCirsIds[ referenceSet.id ] !== 'src' )
                        || ( direction === 'DestToSrc' && dropNode.data.allowedPayloadCirsIds[ referenceSet.id ] !== 'dest' )
                    ) {
                        const srcItem     = direction === 'SrcToDest' ? dragItem! : dropItem!,
                              destItem    = direction === 'SrcToDest' ? dropItem! : dragItem!,
                              checkResult = dropEffect === 'copy' ? { cardinalityCheck: true } : checkCardinalityFeOnly( this.dataContext, this.payloadSchema, referenceSet.id, srcItem, destItem, dropEffect === 'move' ? dragNode.data.payloadReferenceId : undefined );

                        if ( checkResult.cardinalityCheck ) {
                            ( retVal as ReferenceDescription[] ).push( {
                                srcItemId        : srcItem.id,
                                destItemId       : destItem.id,
                                referenceSetId   : referenceSet.id,
                                referenceTypeName: referenceSet.referenceType.name,
                                idx              : ( retVal as ReferenceDescription[] ).length,
                                direction
                            } );
                        }
                    }
                }
            }
        }

        return retVal.length ? retVal : false;
    }

    /**
     * Update node data based on new position in tree
     * @param {TreeNode} droppedNode the dropped node
     * @param {TreeNode} dropTargetNode the drop target
     * @param {String} payloadReferenceId the payload reference id
     */
    updateDroppedNode( droppedNode : ExtendTreeNode, dropTargetNode : ExtendTreeNode, payloadReferenceId : number ) {
        droppedNode.data.payloadReferenceId = payloadReferenceId;
        droppedNode.data.parentNodeId = dropTargetNode.id;
    }

    /**
     * Get node from flatNodeMap by id
     * @param nodeId
     */
    getNodeById( nodeId : number | string ) : ExtendTreeNode | undefined {
        return this.flatNodeMap?.[ nodeId ];
    }

    /**
     * Get node from flatNodeMap by id
     * @param nodeId
     */
    getChildNodesById( nodeId : number | string ) : ExtendTreeNode[] {
        const retVal : ExtendTreeNode[] = [];
        const parentNode = this.flatNodeMap?.[ nodeId ];

        const getChildNodes = ( node : ExtendTreeNode ) => {
            if ( node.children?.length ) {
                for ( const child of node.children ) {
                    if ( child.data.payloadId && !retVal.includes( child ) ) {
                        retVal.push( child );
                    }
                    getChildNodes( child );
                }
            }
        }

        if ( parentNode ) {
            getChildNodes( parentNode );
        }

        return retVal;
    }

    /**
     * Get node from flatNodeMap by id
     * @param nodeId
     */
    getNodesByOrigId( origId : number ) : NodeMap {
        return this.flatNodeMap
            ? Object.values( this.flatNodeMap ).filter( node => node.data.itemId === origId )
                .reduce( ( retVal, node ) => {
                    retVal[ node.id ] = node;
                    return retVal;
                }, {} as NodeMap )
            : {};
    }

    /**
     * Get allowed cirsIds for a given node.
     * Uses data.allowedCirsIds property of DynamicNodes and looks down for dynamic children of TreeNodes
     * @param nodeId
     */
    getAllowedCirsIdForNode( nodeId : number | string ) : ReferenceSetsWithDirection {
        let retVal : ReferenceSetsWithDirection = {};
        const node          = this.getNodeById( nodeId ),
              isDynamicNode = node?.cypherMatchNum !== undefined;

        if ( node ) {
            // get allowed cirs ids from node data...
            if ( node.data?.allowedPayloadCirsIds ) {
                retVal = node.data.allowedPayloadCirsIds;
            }

            if ( !isDynamicNode ) {
                // ... and the cirs ids covered by the child nodes
                for ( const child of node.children ?? [] ) {
                    if ( child.data?.allowedPayloadCirsIds ) {
                        retVal = {
                            ...retVal,
                            ...child.data.allowedPayloadCirsIds
                        };
                    }
                }
            }
        }

        return retVal;
    }

    getAllowedPayloadCirsIdsForNode( node : ExtendTreeNode ) {
        let retVal : { [ cirsId : number ] : 'dest' | 'src' } = {},
            dataObjectNodes : string[]                        = [];

        // collect dataObjects of child nodes
        node.children?.forEach( child => {
            dataObjectNodes = dataObjectNodes.concat( this.dataObjectMapping?.[ child.origId ?? 0 ] ?? [] );
        } );

        dataObjectNodes.forEach( doNodeId => {
            const dataObject = getDataObject( doNodeId, this.dataStructure );

            if ( dataObject?.referenceTarget?.payloadCirsIds ) {
                const direction = dataObject.referenceTarget.referenceDirection === 'SrcToDest'
                    ? 'dest'
                    : 'src';

                retVal = {
                    ...retVal,
                    ...dataObject.referenceTarget.payloadCirsIds.reduce(
                        ( result : { [ cirsId : number ] : 'dest' | 'src' }, cirsId : number ) : {
                            [ cirsId : number ] : 'dest' | 'src'
                        } => {
                            result[ cirsId ] = direction;
                            return result;
                        },
                        {}
                    )
                };
            }
        } );

        return retVal;
    }

    filterAllowedCirsIdsForNode( allowedCirsIdsForNode : ReferenceSetsWithDirection, nodeId : number | string, dataContext : string ) : ReferenceSetsWithDirection {
        const retVal = { ...allowedCirsIdsForNode };

        if ( Object.keys( retVal ).length ) {
            const itemId = this.getPayloadIdForNode( nodeId ) ?? 0,
                  item   = global.data[ dataContext ]?.items[ itemId ],
                  schema = global.data[ dataContext ]?.schema;

            // check for cardinality of existing references
            if ( item && schema ) {
                for ( const rSetId of Object.keys( retVal ) ) {
                    const referenceSetId = Number( rSetId ),
                          referenceSet   = metaModel[ schema ].getReferenceSet( referenceSetId );

                    if ( referenceSet ) {
                        if ( referenceSet.srcItemTypeId !== item.itemTypeId && referenceSet.destItemTypeId !== item.itemTypeId ) {
                            delete retVal[ rSetId ];
                        }
                        else if ( retVal?.[ rSetId ] === 'src' && ( referenceSet.cardinality === 'n:1' || referenceSet.cardinality === '1:1' ) ) {
                            for ( const cardinalityCheckReferenceId of item.references ) {
                                const cardinalityCheckReference = global.data[ dataContext ]?.references[ cardinalityCheckReferenceId ];

                                if ( cardinalityCheckReference && cardinalityCheckReference.referenceSet.id === referenceSetId && !cardinalityCheckReference.isDeleted() && cardinalityCheckReference.srcItemId === item.id ) {
                                    delete retVal[ rSetId ];
                                    break;
                                }
                            }
                        }
                        else if ( retVal?.[ rSetId ] === 'dest' && ( referenceSet.cardinality === '1:n' || referenceSet.cardinality === '1:1' ) ) {
                            for ( const cardinalityCheckReferenceId of item.references ) {
                                const cardinalityCheckReference = global.data[ dataContext ]?.references[ cardinalityCheckReferenceId ];

                                if ( cardinalityCheckReference && cardinalityCheckReference.referenceSet.id === referenceSetId && !cardinalityCheckReference.isDeleted() && cardinalityCheckReference.destItemId === item.id ) {
                                    delete retVal[ rSetId ];
                                    break;
                                }
                            }
                        }
                    }
                }
            }
        }

        return retVal;
    }

    getAllowedReferenceSetIdsOfNodeItem( nodeId : number | string, dataContext : string ) : ReferenceSetsWithDirection {
        const node         = this.getNodeById( nodeId ),
              staticNodeId = Object.entries( this.dataObjectMapping ).find( e => e[ 1 ].includes( node?.origId ?? '' ) )?.[ 0 ],
              staticNode   = this.flatStaticNodeMap?.[ staticNodeId ?? node?.origId ?? '' ];

        let retVal : ReferenceSetsWithDirection | undefined = undefined;

        if ( node?.data.isRecursive ) {
            const parentNode = this.getNodeById( node.data.parentNodeId );

            if ( parentNode ) {
                retVal = {
                    ...this.getAllowedPayloadCirsIdsForNode( node ),
                    ...this.getAllowedPayloadCirsIdsForNode( parentNode )
                };
            }
        }

        if ( !retVal ) {
            retVal = {
                ...staticNode?.data.allowedPayloadCirsIds,
                ...this.getAllowedCirsIdForNode( nodeId )
            };
        }

        retVal = this.filterAllowedCirsIdsForNode(
            retVal!,
            nodeId,
            dataContext
        );

        return retVal;
    }

    /**
     * Get all DynamicNode children of a given node
     * @param node
     * @param fromOriginal if true, use the original TreeNode or DynmicNode otherwise the node generated for the current tree
     */
    getDynamicChildren( node : ExtendTreeNode, fromOriginal : boolean = true ) : ExtendTreeNode[] {
        let retVal : ExtendTreeNode[] = [];
        const parentNode = fromOriginal ? this.flatStaticNodeMap?.[ node.origId ?? '' ] : node;

        if ( parentNode ) {
            retVal = parentNode.children?.filter( child => child.isDynamicNode ?? child.id.match( /^dn_/ ) ) ?? [];
        }

        return retVal;
    }

    /**
     * Get add permission for node by checking permissions of DynanmicNode children
     * @param node
     */
    getAddPermissions( node : ExtendTreeNode ) : Partial<NodePermissions> {
        const retVal : Partial<NodePermissions> = {};
        for ( const child of this.getDynamicChildren( node ) ) {
            if ( child.data.ciItem.disableAddExistingItem !== undefined ) {
                retVal.disableAddExistingItem = ( retVal.disableAddExistingItem === undefined || retVal.disableAddExistingItem ) && child.data.ciItem.disableAddExistingItem;
            }
            if ( child.data.ciItem.disableAddNewItem !== undefined ) {
                retVal.disableAddNewItem = ( retVal.disableAddNewItem === undefined || retVal.disableAddNewItem ) && child.data.ciItem.disableAddNewItem;
            }
        }
        return retVal;
    }

    /**
     * Get conditional permission for add/clone/delete a tree node
     * @param node
     */
    getConditionalNodePermissions( node : ExtendTreeNode, basePermissions : NodePermissions ) : Partial<NodePermissions> {
        const retVal : Partial<NodePermissions> = { ...basePermissions };

        // only check if at least one permission is set in basePermissions
        if ( !retVal.disableAddExistingItem || !retVal.disableAddNewItem || !retVal.disableDeleteItem || !retVal.disableDeleteReference || retVal.allowClone ) {

            const relevantAddCirsIds = [
                this.isDisablingAddExistingItemForDynamicNodeReferenceSetId,
                this.isDisablingAddNewItemForDynamicNodeReferenceSetId
            ];

            const relevantDeleteCirsIds = [
                this.isDisablingDeleteItemForDynamicNodeReferenceSetId,
                this.isDisablingDeleteReferenceForDynamicNodeReferenceSetId
            ];

            const cirsIdToFlagMap : { [ cirsId : number ] : keyof typeof retVal } = {
                [ this.isDisablingAddExistingItemForDynamicNodeReferenceSetId ]: 'disableAddExistingItem',
                [ this.isDisablingAddNewItemForDynamicNodeReferenceSetId ]     : 'disableAddNewItem',
                [ this.isDisablingDeleteItemForDynamicNodeReferenceSetId ]     : 'disableDeleteItem',
                [ this.isDisablingDeleteReferenceForDynamicNodeReferenceSetId ]: 'disableDeleteReference'
            }

            // get related delete conditions
            let conditionReferences = dataUtils.findReferences( this.metaContext, relevantDeleteCirsIds, undefined, node?.data.itemId );
            if ( conditionReferences?.length ) {
                for ( const conditionReference of conditionReferences ) {
                    const conditionItem = global.data[ this.metaContext ]?.items[ conditionReference.srcItemId ],
                          payloadItem   = global.data[ this.dataContext ]?.items[ node?.data.payloadId ?? node?.data.delegatedPayloadId ];

                    // apply condition to allow flag
                    if ( conditionItem && payloadItem && !basePermissions[ cirsIdToFlagMap[ conditionReference.referenceSetId ] ] ) {
                        retVal[ cirsIdToFlagMap[ conditionReference.referenceSetId ] ] = this.resolveCondition( conditionItem, payloadItem );
                    }
                }
            }

            // get related add conditions
            for ( const child of this.getDynamicChildren( node, false ) ) {
                conditionReferences = dataUtils.findReferences( this.metaContext, relevantAddCirsIds, undefined, child.data.itemId );

                if ( conditionReferences?.length ) {
                    for ( const conditionReference of conditionReferences ) {
                        const conditionItem = global.data[ this.metaContext ]?.items[ conditionReference.srcItemId ],
                              payloadItem   = global.data[ this.dataContext ]?.items[ child.data.payloadId ?? child.data.delegatedPayloadId ];

                        // apply condition to allow flag
                        if ( conditionItem && payloadItem && !basePermissions[ cirsIdToFlagMap[ conditionReference.referenceSetId ] ] ) {
                            retVal[ cirsIdToFlagMap[ conditionReference.referenceSetId ] ] =
                                ( retVal[ cirsIdToFlagMap[ conditionReference.referenceSetId ] ] === undefined || retVal[ cirsIdToFlagMap[ conditionReference.referenceSetId ] ] ) && this.resolveCondition( conditionItem, payloadItem );
                        }
                    }
                }
            }

            // for now, we don't have a dedicated allowClone condition
            retVal.allowClone = basePermissions.allowClone && !retVal.disableAddNewItem;

        }
        return retVal;
    }

    /**
     * Get payloadId of a given node.
     * Uses data.payloadId property for DynamicNodes and looks up for first DynamicNode from TreeNodes
     * @param nodeId
     */
    getPayloadIdForNode( nodeId : number | string ) : number | undefined {
        let retVal;
        let node = this.getNodeById( nodeId );
        const checkedNodes : { [ id : string ] : boolean } = {};
        while ( node && !checkedNodes[ node.id ] && !retVal ) {
            checkedNodes[ node.id ] = true;
            retVal = node.data?.payloadId ?? node.data?.delegatedPayloadId;
            if ( !retVal ) {
                node = this.getNodeById( node.data?.parentNodeId );
            }
        }
        return retVal;
    }

    /**
     * Get the parent payloadId of a given node.
     * Uses data.payloadId property for DynamicNodes and looks up for first DynamicNode from TreeNodes
     * @param nodeId
     */
    getParentPayloadIdForNode( nodeId : number | string ) : number | undefined {
        let retVal;
        const node = this.getNodeById( nodeId );
        let parentNode = this.getNodeById( node?.data?.parentNodeId );

        const checkedNodes : { [ id : string ] : boolean } = {};
        while ( parentNode && !checkedNodes[ parentNode.id ] && !retVal ) {
            checkedNodes[ parentNode.id ] = true;
            retVal = parentNode.data?.payloadId;
            if ( !retVal ) {
                parentNode = this.getNodeById( parentNode.data?.parentNodeId );
            }
        }
        return retVal;
    }

    /**
     * Get the key of the DataObject item that was used to load the given item
     * @param payloadId
     */
    getDataObjectKeyForPayloadId( payloadId : number ) : string {
        let retVal = '';
        const dataObjectNodeId = this.payloadIdToDataObjectNodeIdMap[ payloadId ]?.[ 0 ];
        if ( dataObjectNodeId ) {
            retVal = this.flatDataObjectNodeMap?.[ dataObjectNodeId ]?.data.ciItem.key ?? '';
        }
        return retVal;
    }

    /**
     * Build flat node id to node map
     * @param node
     * @return {{}}
     */
    getFlatNodeMap( node : ExtendTreeNode ) : NodeMap {
        const retVal = {
                  [ node.id! ]: node
              },
              nodes  = Array.isArray( node )
                  ? node
                  : node.children ?? [];

        for ( const n of nodes ) {
            Object.assign( retVal, this.getFlatNodeMap( n ) );
        }

        return retVal;
    }

    /**
     * Get relevant references between DataObjects and TreeNodes (the left side of ViewDesigner)
     */
    getItemTypeGroupsReferencedToDataObject() : { [ groupName : string ] : number[] } {
        const retVal : { [ groupName : string ] : number[] } = {};

        for ( const itemTypeName in this.dataObjectToTreeNodeReferenceSetIds ) {
            // check if referenceSet belongs to DataObject
            outer: for ( const referenceSetId of this.dataObjectToTreeNodeReferenceSetIds[ itemTypeName ] ) {
                const referenceSet = metaModel[ 'CFG' ].getReferenceSet( referenceSetId );

                if (
                    (
                        referenceSet?.srcItemType.name === 'DataObject'
                        || referenceSet?.destItemType.name === 'DataObject'
                    )
                    && referenceSet.referenceType.name !== 'isReferencedTo'
                ) {

                    // check if any reference for referenceSet is loaded
                    for ( const reference of Object.values( global.data[ this.dataContext ]?.references ?? {} ) ) {
                        if ( reference.referenceSetId === referenceSet.id ) {
                            // TODO: for now we are just group itemTypes with name ending on Field, this should be made configurable
                            const groupName : string = itemTypeName.endsWith( 'Field' ) ? 'Field' : itemTypeName;

                            retVal[ groupName ] = retVal[ groupName ] ?? []
                            retVal[ groupName ] = retVal[ groupName ].concat( this.dataObjectToTreeNodeReferenceSetIds[ itemTypeName ] );

                            break outer;
                        }
                    }
                }
            }
        }

        return retVal;
    }

    /**
     * Compares property value of two items for array sort
     * @param {String} propertyTypeName
     * @param {Item} item1
     * @param {Item} item2
     * @return {Number}
     */
    propertyCompare( propertyTypeName : string, item1 : Item, item2 : Item ) : 0 | 1 | -1 {
        let retVal : 0 | 1 | -1 = 0;
        const propertyTypeId = metaModel[ this.payloadSchema ].getPropertyType( propertyTypeName )?.id;

        if ( propertyTypeId && item1.properties.get( propertyTypeId ) && item2.properties.get( propertyTypeId ) ) {
            retVal = ( item1.properties.get( propertyTypeId )?.value ?? 0 ) < ( item2.properties.get( propertyTypeId )?.value ?? 0 )
                ? -1
                : ( ( item1.properties.get( propertyTypeId )?.value ?? 0 ) > ( item2.properties.get( propertyTypeId )?.value ?? 0 ) ? 1 : 0 );
        }
        return retVal;
    }

    /**
     * TODO: this one is unused. Check and remove
     * @param cypherConfig
     * @param matchNum
     */
    getCirsIdsFromCypherConfig( cypherConfig : ProcessResultPart[], matchNum : number ) : { [ cirsId : number ] : string } | undefined {
        const refConfig   = cypherConfig[ matchNum ]?.recursionConfigs?.[ 1 ]?.reference,
              whereConfig = cypherConfig[ matchNum ]?.whereConfig;

        let excludedItemTypeId = 0;
        if ( whereConfig?.op === 'not-equal' && whereConfig?.arg1?.field === 'itemTypeId' && whereConfig?.arg1?.value ) {
            excludedItemTypeId = whereConfig.arg1?.value as number;
        }
        return refConfig
            ? refConfig.computedIds?.referenceSetIds.reduce( ( result : { [ cirsId : number ] : string }, cirsId : number ) : {
                [ cirsId : number ] : string
            } => {
                let allowRefSet = true;
                if ( excludedItemTypeId ) {
                    const rset = metaModel[ this.payloadSchema ].getReferenceSet( cirsId );
                    allowRefSet = rset?.srcItemTypeId !== excludedItemTypeId && rset?.destItemTypeId !== excludedItemTypeId;
                }
                if ( allowRefSet ) {
                    result[ cirsId ] = refConfig.direction;
                }
                return result;
            }, {} )
            : undefined;
    }

    /**
     * Get data for DynamicNodes
     * @param dataObjectNode the dataObjectNode
     * @param parentDataObjectNode the parent dataObjectNode
     * @param payloadCypherConfig the payload cypher config, describing the relation of payload data
     */
    getPayloadData( dataObjectNode : ExtendTreeNode, parentDataObjectNode : ExtendTreeNode | undefined, payloadCypherConfig : any ) : PayloadData[] {

        const payloadData : { [ id : number ] : PayloadData } = {};

        // root node
        if ( !parentDataObjectNode ) {
            const ciItem = global.data[ this.dataContext ]?.items[ dataObjectNode.data.payloadId ];
            if ( ciItem ) {
                payloadData[ ciItem.id ] = {
                    dataObjectNode    : dataObjectNode,
                    ciItem,
                    referenceId       : 0,
                    cypherMatchNum    : 0,
                    payloadIdPath     : '-' + ciItem.id,
                    referenceDirection: 'SrcToDest',
                    isRecursive       : false
                };
            }
        }
        // if reference target is set, we can collect the payload data that's connected with this node
        else if ( dataObjectNode.referenceTarget ) {

            // get targetNodes for this reference
            const referenceTargetNodes : ExtendTreeNode[] = []
                .concat( this.flatNodeMap?.[ dataObjectNode.referenceTarget.destNodeId ] ?? undefined as any )
                .concat( this.siblingNodeMap?.[ dataObjectNode.referenceTarget.destNodeId ] ?? undefined as any );

            const { cypherMatchNum, referenceDirection, stopped } = dataObjectNode.referenceTarget;

            // treeNode.delegatedPayloadId für resume load
            const excludedObjectType = dataObjectNode.data.ciItem.objectType.startsWith( '!' ) ? dataObjectNode.data.ciItem.objectType.slice( 1 ) : false;

            dataObjectNode.childrenCypherMatchNums = dataObjectNode.childrenCypherMatchNums ?? [];
            dataObjectNode.childrenCypherMatchNums.push( cypherMatchNum );
            dataObjectNode.childrenCypherMatchNums = _.uniq( dataObjectNode.childrenCypherMatchNums );

            // collect payload CiData and cypherInfo for each referenceTarget
            for ( const referenceTargetNode of referenceTargetNodes ) {
                if ( referenceTargetNode ) {
                    const payloadId = referenceTargetNode.data.payloadId;
                    const payloadIdPath = referenceTargetNode.data.payloadIdPath;

                    // get payload data with HierarchyBuilder and cypher config of the corresponding match clause
                    if ( payloadId && this.payloadItems?.[ payloadId ] ) {
                        getHierarchy(
                            this.dataContext,
                            this.payloadSchema,
                            this.payloadItems?.[ payloadId ],
                            [ payloadCypherConfig[ cypherMatchNum ] ],
                            payloadData,
                            // callback will be called for each found item and returns the payloadData generated for it
                            ( ciItem : Item, parentNode : PayloadData, referenceId : number, referenceSetId : number ) => {
                                let retVal : PayloadData | PayloadData[] = [];
                                // obey all not delete items for now
                                if ( referenceId && ciItem.itemType.name !== excludedObjectType ) {
                                    const reference = global.data[ this.dataContext ]?.references[ referenceId ];
                                    // Don't show nodes with active items at deleted references, but show delete items node to be able to undelete them
                                    // (solution after discussion with FS due to issues when drag'n'drop (moving) node around, see #)
                                    if ( !reference.isDeleted() || ciItem.isDeleted() ) {
                                        retVal = {
                                            dataObjectNode: dataObjectNode,
                                            ciItem,
                                            referenceId,
                                            cypherMatchNum,
                                            stopped,
                                            payloadIdPath,
                                            referenceDirection,
                                            isRecursive   : !!payloadCypherConfig[ cypherMatchNum ]?.recursionConfigs[ 1 ]?.reference?.recursion
                                        };
                                        // prevent infinite loops
                                        if ( referenceId !== parentNode.referenceId && dataObjectNode.referenceTarget?.payloadCirsIds.includes( reference.referenceSetId ) ) {
                                            if ( _.isArray( parentNode ) ) {
                                                payloadData[ reference.id ] = retVal;
                                            }
                                            else {
                                                parentNode.children = parentNode.children ?? [];
                                                parentNode.children.push( retVal );
                                            }
                                        }
                                    }
                                }
                                return retVal;
                            }, 0, false, [], false, true
                        );
                    }
                }
            }
        }

        return Object.values( payloadData );
    }

    /**
     * Create PayloadNodes for tree based on the dynamic branch data recursively
     * @param payloadData the dynamic branch data
     */
    createPayloadBranch( payloadData : PayloadData[], parentPayloadIdPath? : string ) : {
        [ idPath : string ] : ExtendTreeNode[]
    } | undefined {
        const retVal : { [ idPath : string ] : ExtendTreeNode[] } = {};

        for ( const pD of payloadData ) {
            const {
                      children,
                      ciItem,
                      cypherMatchNum,
                      dataObjectNode,
                      isRecursive,
                      referenceDirection,
                      referenceId,
                      stopped
                  }                = pD,
                  payloadIdPath    = pD.payloadIdPath + ( parentPayloadIdPath ? '-' + parentPayloadIdPath : '' ),
                  newPayloadIdPath = payloadIdPath + '-' + ciItem.id,
                  md5Part          = '_' + md5( ciItem.itemType.name + '_' + ciItem.id );

            let count = 0;

            this.payloadIdToDataObjectNodeIdMap[ ciItem.id ] = this.payloadIdToDataObjectNodeIdMap[ ciItem.id ] ?? [];
            while ( this.payloadIdToDataObjectNodeIdMap[ ciItem.id ].includes( dataObjectNode.id + ( count ? '_' + count : '' ) + md5Part ) ) {
                count++;
            }

            const reference    = global.data[ this.dataContext ]?.references[ referenceId ],
                  // double check: does the given path exists?
                  lastRefParts = newPayloadIdPath.split( /-(-?\d+)/ ).filter( id => !!id );

            if ( lastRefParts.length === 2 && ciItem.id !== this.payloadRootItemId && !dataUtils.findReferences( this.dataContext, undefined, Number( lastRefParts[ 0 ] ), Number( lastRefParts[ 1 ] ) ) ) {
                return;
            }

            const nodeIsDirty   = ciItem.isNew() || ciItem.isModified() || reference?.isNew() || reference?.isModified(),
                  nodeIsDeleted = ciItem.isDeleted(),
                  newNodeId     = dataObjectNode.id + ( count ? '_' + count : '' ) + md5Part;

            this.payloadIdToDataObjectNodeIdMap[ ciItem.id ].push( newNodeId );

            const nodeDefaults             = {
                      ...dataExtraction.getDefaultsForItemType( 'CFG', 'DynamicNode' )
                  },
                  newNode : ExtendTreeNode = {
                      id: newNodeId,
                      // TODO: siblingId not used, consider to set just a "isDynamicNode" flag
                      siblingId: dataObjectNode.siblingId ?? dataObjectNode.id,
                      key      : newNodeId,
                      // these are the default values, are getting overwritten in @see #decorateDynamicNode
                      label              : ciItem.displayAttributeValue,
                      icon               : resolveLigatureIcon( 'yn-tree-icon icon-itemtype-detail ' + ciItem.itemType.icon ),
                      className          : ( nodeIsDeleted ? 'yn-deleted' : '' ) + ( nodeIsDirty ? ' yn-modified ' : '' ),
                      maxAddButtonCount  : nodeDefaults.maxAddButtonCount,
                      draggable          : true,
                      droppable          : true,
                      keepReferenceOnDrop: dataObjectNode.referenceTarget ? dataObjectNode.referenceTarget.keepReferenceOnDrop : false,
                      cypherMatchNum,
                      stopped,
                      data               : Object.assign( {
                          payloadId         : ciItem.id,
                          payloadIdPath     : newPayloadIdPath,
                          payloadReferenceId: referenceId,
                          referenceDirection,
                          isDirty           : nodeIsDirty,
                          isDeleted         : nodeIsDeleted,
                          isRecursive
                      }, dataObjectNode.data )
                  },
                  doss                     = Object.keys( this.dataObjectMapping )
                      .map( key => this.dataObjectMapping[ key ] )
                      .filter( dos => dos.includes( dataObjectNode.id ) && !dos.includes( newNodeId ) );

            for ( const dos of doss ) {
                dos.push( newNodeId );
            }

            if ( children ) {
                newNode.payload = this.createPayloadBranch( children, ( parentPayloadIdPath ? parentPayloadIdPath + '-' : '' ) + ciItem.id );
            }

            retVal[ payloadIdPath ] = retVal[ payloadIdPath ] || [];
            retVal[ payloadIdPath ].push( newNode );

            this.flatNodeMap![ newNode.id! ] = newNode;
            // TODO: check if siblingNodeMap is still used?
            if ( newNode.siblingId ) {
                this.siblingNodeMap![ newNode.siblingId ] =
                    ( [] as ExtendTreeNode[] ).concat( this.siblingNodeMap![ newNode.siblingId ] ).concat( newNode );
            }
        }

        return retVal;
    }

    /**
     * Apply payload data (dynamic node) to dataObjectNodes
     * @param {TreeNode} dataObjectNode
     * @param {TreeNode} parentDataObjectNode
     * @param {Object} payloadCypherConfig
     */
    applyDynamicNodes( dataObjectNode : ExtendTreeNode, parentDataObjectNode : ExtendTreeNode | undefined, payloadCypherConfig : any ) {

        const payloadData = this.getPayloadData( dataObjectNode, parentDataObjectNode, payloadCypherConfig );

        const payloadBranch = this.createPayloadBranch( payloadData );

        dataObjectNode.payload = payloadBranch;

        if ( dataObjectNode.children ) {
            // apply dynamic nodes to all current children recursively
            // eslint-disable-next-line @typescript-eslint/prefer-for-of
            for ( let i = 0; i < dataObjectNode.children.length; i++ ) {
                const childNode = dataObjectNode.children[ i ];
                this.applyDynamicNodes( childNode, dataObjectNode, payloadCypherConfig );
            }
        }
    }

    markNodeClean( node : ExtendTreeNode ) {
        if ( ( node.className ?? '' ).includes( 'yn-modified-children' ) ) {
            node.className = node.className?.replace( ' yn-modified-children', '' );
        }
    }

    markPathDirty( node : ExtendTreeNode ) {
        if ( node.data?.parentNodeId ) {
            const parentNode = this.getNodeById( node.data.parentNodeId );

            if ( parentNode ) {
                if ( !( parentNode.className ?? '' ).includes( 'yn-modified-children' ) ) {
                    parentNode.className = ( parentNode.className ?? '' ) + ' yn-modified-children';
                }

                if ( parentNode.data.parentNodeId && parentNode.data.parentNodeId !== parentNode.id ) {
                    this.markPathDirty( parentNode );
                }
            }
        }
    };

    markPathValidOrInvalid( node : ExtendTreeNode, invalidNodeIds : { [ nodeId : string ] : true }, errorData? : ErrorData ) {
        if ( node.data?.parentNodeId ) {
            const parentNode = this.getNodeById( node.data.parentNodeId );

            if ( parentNode ) {
                if ( errorData ) {
                    if ( errorData?.count ) {
                        invalidNodeIds[ parentNode.id ] = true;
                        if ( !( parentNode.className ?? '' ).includes( 'yn-invalid-children' ) ) {
                            parentNode.className = ( parentNode.className ?? '' ) + ' yn-invalid-children';
                        }
                    }
                    else if ( !invalidNodeIds[ parentNode.id ] ) {
                        parentNode.className = ( parentNode.className ?? '' ).replace( ' yn-invalid-children', '' );
                    }
                }

                if ( parentNode.data.parentNodeId && parentNode.data.parentNodeId !== parentNode.id ) {
                    this.markPathValidOrInvalid( parentNode, invalidNodeIds, errorData );
                }
            }
        }
    };

    /**
     * Ger PayloadNode based on dataObjectNode and dynamicNode with unique id/key
     * @param dataObjectNode
     * @param dynamicNode
     * @param parentNode
     */
    getPayloadNode( dataObjectNode : ExtendTreeNode, dynamicNode : ExtendTreeNode, parentNode : ExtendTreeNode ) : ExtendTreeNode {
        const dynamicNodeData = _.pick( dynamicNode.data, [ 'allowedPayloadCirsIds', 'ciItem', 'itemId', 'parentNodeId' ] );
        const id = 'tnc_' + md5( dynamicNode.id + '_' + dataObjectNode.id + parentNode.id );
        const clonedChild = {
            ...dataObjectNode, children: dynamicNode.children?.map( o => {
                return { ...o };
            } ), data                  : { ...dataObjectNode.data, ...dynamicNodeData }
        };
        clonedChild.origId = dynamicNode.id;
        clonedChild.id = id;
        clonedChild.key = clonedChild.id;
        clonedChild.visibility = dynamicNode.visibility;

        this.payloadIdToDynamicNodeIdMap[ dataObjectNode.data.payloadId ] = this.payloadIdToDynamicNodeIdMap[ dataObjectNode.data.payloadId ] ?? [];
        this.payloadIdToDynamicNodeIdMap[ dataObjectNode.data.payloadId ].push( id );
        this.payloadReferenceIdToDynamicNodeIdMap[ dataObjectNode.data.payloadReferenceId ] = this.payloadReferenceIdToDynamicNodeIdMap[ dataObjectNode.data.payloadReferenceId ] ?? [];
        this.payloadReferenceIdToDynamicNodeIdMap[ dataObjectNode.data.payloadReferenceId ].push( id );

        // static nodes derived from a dynamic node also can be used as drop target.
        // so add the payloadId as delegated.
        clonedChild.data.delegatedPayloadId = dataObjectNode.data.payloadId;
        clonedChild.data.parentNodeId = parentNode?.id;
        //this.dataObjectMapping[ clonedChild.id ] = this.dataObjectMapping[ dynamicNode.id ];
        this.flatNodeMap![ clonedChild.id ] = clonedChild;
        return clonedChild;
    };

    /**
     * Get copy from static treeNode with unique id/key and payload ids
     * @param treeNode
     * @param parentNode
     */
    getStaticNode( treeNode : ExtendTreeNode, parentNode? : ExtendTreeNode ) : ExtendTreeNode {
        const id = 'tn_' + md5( treeNode.id + ( parentNode ? '_' + parentNode.id : '' ) );

        treeNode.origId = treeNode.id;
        treeNode.id = id;
        treeNode.key = id;
        this.flatNodeMap![ treeNode.id ] = treeNode;

        if ( parentNode ) {
            treeNode.data = { ...treeNode.data };
            treeNode.data.parentNodeId = parentNode.id;
            treeNode.data.delegatedPayloadId = parentNode.data.payloadId ?? parentNode.data.delegatedPayloadId;
        }

        treeNode.droppable = !!treeNode.data.delegatedPayloadId;

        // make static nodes only selectable if they got a panel config (e.g. for an overview)
        const hasPanelConfig = !!dataUtils.findReferences( this.metaContext, this.panelConfigBelongsToTreeNodeCirsId, undefined, treeNode.data.itemId )?.length;
        treeNode.selectable = hasPanelConfig;

        return treeNode;
    };

    /**
     * Detect if a node represents a cyclic payload item (has parent with same payload id)
     * @param node
     * @param payloadId
     */
    isCyclic( node : ExtendTreeNode, payloadId? : number ) : boolean {
        const parentNode = this.flatNodeMap![ node.data?.parentNodeId ];
        payloadId = payloadId ?? node.data?.payloadId;
        return parentNode && parentNode.id !== node.id ? parentNode.data?.payloadId === payloadId || this.isCyclic( parentNode, payloadId ) : false;
    }

    /**
     * Merge payload nodes to appropriate DynamicNode in tree
     * @param treeNode TreeNode, DynamicNode
     * @param parentTreeNode
     * @param payloadIdPath
     */
    mergeStaticTreeAndDataObject( treeNode : ExtendTreeNode, parentTreeNode? : ExtendTreeNode, payloadIdPath = '' ) : ExtendTreeNode | ExtendTreeNode[] | undefined {
        let retVal : ExtendTreeNode | ExtendTreeNode[] | undefined = treeNode;

        const isDynamicNode = !treeNode.id.startsWith( 'tn_' ) && !treeNode.id.startsWith( 'tv_' );

        // wenn treeNode ein statisches Node ist, einfach durch dessen children iterieren und weiter

        // dynamische nodes durch die entsprechenden Payload Nodes ersetzen und durch deren children iterieren

        const isNotUndefined = ( obj : any ) : obj is ExtendTreeNode => {
            return !!obj;
        }

        let payloadReplacement : ExtendTreeNode[] = [];
        const matchingPayloadIdPaths : string[] = [];

        // remove hidden nodes
        if ( treeNode?.visibility === 'hidden' ) {
            retVal = undefined;
        }
        else {

            if ( isDynamicNode ) {

                const coveredItemTypeIds : number[] = [];

                // replace DynamicNode by payload
                if ( this.dataObjectMapping[ treeNode.origId ?? treeNode.id ]?.length ) {
                    for ( const doNodeId of this.dataObjectMapping[ treeNode.origId ?? treeNode.id ] ) {
                        const dataObjectNode = this.getNodeById( doNodeId );

                        const currentItemTypeId = metaModel[ this.payloadSchema ].getItemType( dataObjectNode?.data.ciItem.objectType )?.id;
                        if ( currentItemTypeId && !coveredItemTypeIds.includes( currentItemTypeId ) ) {
                            coveredItemTypeIds.push( currentItemTypeId );
                        }

                        // check if the parent DataObject has references to DynamicNodes,
                        // if not it means that the parent is just for data organisation, and we need to extend our payloadIdPaths (over multiple levels)
                        const parentDataObjectNode : ExtendTreeNode | undefined = this.getNodeById( dataObjectNode?.data.parentNodeId );
                        let relatedDynamicNodeRefs : Reference[] | undefined;

                        const getParentPayloadIdPaths = ( parentDONode : ExtendTreeNode, extendedPayloadIdPath : string = '' ) : string[] => {
                            let innerRetVal : string[] = [];
                            relatedDynamicNodeRefs = Object.hasOwn( this.relatedDynamicNodeRefsCache.hasOwnProperty, parentDONode?.data?.ciItem.id )
                                ? this.relatedDynamicNodeRefsCache[ parentDONode?.data?.ciItem.id ]
                                : ( this.relatedDynamicNodeRefsCache[ parentDONode?.data?.ciItem.id ] = dataUtils.findReferences( this.metaContext, this.dataObjectProvidesDataForDynamicNodeCirsId, parentDONode?.data?.ciItem.id ) );
                            if ( !relatedDynamicNodeRefs?.length && parentDONode?.payload ) {
                                const grandParentDataObjectNode = parentDONode?.data?.parentNodeId ? this.getNodeById( parentDONode?.data?.parentNodeId ) : undefined;
                                // build payloadIdPath by adding the payload ids of the parent dataObject to the current payload
                                if ( parentDONode?.payload ) {
                                    for ( const plIdPath of Object.keys( parentDONode.payload ) ) {
                                        const payloadNodes : ExtendTreeNode[] = parentDONode.payload![ plIdPath ];
                                        innerRetVal = payloadNodes.reduce( ( collector : string[], node : ExtendTreeNode ) => {
                                            const newPayloadIdPath = node.data.payloadId + ( extendedPayloadIdPath ? '-' + extendedPayloadIdPath : '' );
                                            collector = collector.concat( payloadIdPath + '-' + newPayloadIdPath );
                                            if ( grandParentDataObjectNode ) {
                                                collector = collector.concat( getParentPayloadIdPaths( grandParentDataObjectNode, newPayloadIdPath ) )
                                            }
                                            return collector;
                                        }, [] );
                                    }
                                }
                            }
                            return innerRetVal;
                        };

                        const parentPayloadIdPaths = parentDataObjectNode ? this.parentPayloadIdPathsCache[ parentDataObjectNode.id ] ?? getParentPayloadIdPaths( parentDataObjectNode ) : [];
                        if ( parentDataObjectNode && !this.parentPayloadIdPathsCache[ parentDataObjectNode.id ] ) {
                            this.parentPayloadIdPathsCache[ parentDataObjectNode.id ] = parentPayloadIdPaths;
                        }

                        if ( dataObjectNode?.payload ) {
                            for ( const plIdPath of Object.keys( dataObjectNode.payload ) ) {
                                const payloadNodes : ExtendTreeNode[] = dataObjectNode.payload![ plIdPath ];
                                for ( const payloadNode of payloadNodes ) {
                                    // only include payload if path matches
                                    if ( plIdPath === payloadIdPath || !payloadIdPath || parentPayloadIdPaths.includes( plIdPath as string ) ) {
                                        payloadReplacement.push( this.getPayloadNode( payloadNode, treeNode, parentTreeNode! ) );
                                        if ( !matchingPayloadIdPaths.includes( payloadIdPath ? plIdPath as string : '' ) ) {
                                            matchingPayloadIdPaths.push( payloadIdPath ? plIdPath as string : '' );
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                payloadReplacement = this.filterPayloadNodes( payloadReplacement, treeNode.data.ciItem );
                this.sortPayloadNodes( payloadReplacement, treeNode.data.ciItem );

            }
            else {
                matchingPayloadIdPaths.push( payloadIdPath );
            }

            ( isDynamicNode ? payloadReplacement : [ retVal ] ).forEach( ( currentNode : ExtendTreeNode ) => {
                let isCyclic : boolean = false;
                if ( isDynamicNode ) {
                    isCyclic = this.isCyclic( currentNode );
                    currentNode.data.isCyclic = isCyclic;
                    this.decoratePayloadNode( currentNode, treeNode );
                }
                else {
                    currentNode = this.getStaticNode( currentNode, parentTreeNode );
                    this.decorateTreeNode( currentNode );
                }
                let newChildren : ExtendTreeNode[] = [];

                // stop if node represents a cyclic payload item
                if ( !isCyclic && currentNode.children ) {
                    for ( const child of currentNode.children ) {
                        const payloadId = currentNode.data?.payloadId ?? '';
                        // set parent node id to all children to enable lookups
                        child.data.parentNodeId = currentNode.id;

                        for ( const matchingPayloadIdPath of matchingPayloadIdPaths ) {
                            const recursiveChildren = this.mergeStaticTreeAndDataObject( child, currentNode, matchingPayloadIdPath + ( payloadId ? '-' + payloadId : '' ) );
                            if ( recursiveChildren ) {
                                newChildren = newChildren.concat( recursiveChildren );
                            }
                        }
                    }

                }

                // allow duplicates on root level (identified by empty payloadIdPath)
                if ( payloadIdPath ) {
                    // do not allow duplicate children

                    // if duplicates exists, prefer the child with the most exact definition (e.g. no * in reference or item type)
                    const orderedChildren = newChildren.sort( ( a, b ) => {
                        let result = 0;

                        if ( a.data?.payloadId && b.data?.payloadId && a.data?.payloadId === b.data?.payloadId ) {
                            const
                                // TODO: check why we can't use the number of coveredCirsIds here
                                precisionA = a.data.ciItem?.objectType === '*' ? 2 : 1, //Object.keys(a.data.coveredCirsIds ?? {} ).length,
                                precisionB = b.data.ciItem?.objectType === '*' ? 2 : 1; //Object.keys(b.data.coveredCirsIds ?? {} ).length;
                            result = precisionA < precisionB ? -1 : ( precisionA > precisionB ? 1 : 0 );
                        }

                        return result;

                    } );

                    // remove duplicates
                    newChildren = _.uniqWith( orderedChildren, ( a, b ) => {
                        // allow duplicate items if they are represented by different DynamicNodes
                        const samePayloadId   = a.data?.payloadId && b.data?.payloadId && a.data?.payloadId === b.data?.payloadId,
                              sameDynamicNode = a.data?.ciItem && b.data?.ciItem && a.data?.ciItem.id === b.data?.ciItem.id;
                        return samePayloadId && sameDynamicNode;
                    } ).filter( isNotUndefined );
                }

                currentNode.children = newChildren;
                currentNode.leaf = !newChildren.length;
            } )

        }

        return !isDynamicNode ? retVal : payloadReplacement;
    }


    getDynamicNodeIdByPayloadId( payloadId : number ) : string[] {
        return this.payloadIdToDynamicNodeIdMap[ payloadId ] ?? [];
    }

    getDynamicNodeIdByPayloadReferenceId( payloadReferenceId : number ) : string[] {
        return this.payloadReferenceIdToDynamicNodeIdMap[ payloadReferenceId ] ?? [];
    }

    /**
     * Add label to TreeNode based on appropriate Message (fallback is label then key of TreeNode)
     * TODO: add data based icon/styling/... also
     * @param payloadNode the payload treenNde
     * @param dynamicNode the DynamicNode from tree metadata
     * @param variables variables to be used in message resolver
     */
    decorateTreeNode( treeNode : ExtendTreeNode, variables? : { [ variable : string ] : any } ) {
        // TODO: discuss what to show if label is empty (e.g. new item without key/name)

        const labelTemplateResolver = messageUtils.buildReturnFunction( { variables: { ...variables } } ),
              styleAndClassName     = this.getNodeStyleAndClassName( treeNode.data.ciItem.id );

        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        treeNode.label = labelTemplateResolver( treeNode.labelKey ? __( treeNode.labelKey ) : treeNode.label ?? '' ) || '...';

        treeNode.style = styleAndClassName.style;

        const hasModifiedChildren = ( treeNode.className ?? '' ).includes( 'yn-modified-children' );
        const hasInvalidChildren = ( treeNode.className ?? '' ).includes( 'yn-invalid-children' );
        treeNode.className = styleAndClassName.className + ( hasModifiedChildren ? ' yn-modified-children' : '' ) + ( hasInvalidChildren ? ' yn-invalid-children' : '' );
        treeNode.icon = this.getNodeIcon( treeNode.data.ciItem.id );
    }

    /**
     * Add label to DynamicNode based on template set in appropriate Message
     * TODO: add data based icon/styling/... also
     * @param payloadNode the payload treeNode
     * @param dynamicNode the DynamicNode from tree metadata
     * @param variables variables to be used in message resolver
     */
    decoratePayloadNode( payloadNode : ExtendTreeNode, dynamicNode? : ExtendTreeNode, variables? : { [ variable : string ] : any } ) {
        const item      = global.data[ this.dataContext ]?.items[ payloadNode.data.payloadId ],
              reference = global.data[ this.dataContext ]?.references[ payloadNode.data.payloadReferenceId ];

        if ( dynamicNode ) {
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            payloadNode.icon = dynamicNode.icon || payloadNode.icon;
            payloadNode.data.labelKey = dynamicNode.labelKey;

            payloadNode.openExpanded = dynamicNode.openExpanded;
            payloadNode.maxAddButtonCount = dynamicNode.maxAddButtonCount;
            payloadNode.customButtonConfig = dynamicNode.customButtonConfig;
        }

        if ( item ) {

            const ignoreDirtyMark       = !!payloadNode.data.ciItem.ignoreDirtyMark,
                  readOnly              = !!payloadNode.data.ciItem.readOnly,
                  nodeIsDirty           = !ignoreDirtyMark && !readOnly && ( item.isNew() || item.isModified() || reference?.isNew() || reference?.isModified() ),
                  nodeIsDeleted         = item.isDeleted(),
                  labelTemplateResolver = messageUtils.getMessageTemplateStringResolver( item, reference, { variables: { ...variables } } ),
                  styleAndClassName     = this.getNodeStyleAndClassName( payloadNode.data.ciItem.id, item );

            payloadNode.label = labelTemplateResolver( payloadNode.data.labelKey ? __( payloadNode.data.labelKey ) : item.displayAttributeValue || '...' ) || item.key;
            const hasModifiedChildren = ( payloadNode.className ?? '' ).includes( 'yn-modified-children' );
            const hasInvalidChildren = ( payloadNode.className ?? '' ).includes( 'yn-invalid-children' );
            payloadNode.className = ( nodeIsDeleted ? ' yn-deleted' : '' ) + ( nodeIsDirty ? ' yn-modified ' : '' ) + styleAndClassName.className + ( hasModifiedChildren ? ' yn-modified-children' : '' ) + ( hasInvalidChildren ? ' yn-invalid-children' : '' );
            payloadNode.data.isDirty = nodeIsDirty;
            payloadNode.data.isDeleted = nodeIsDeleted;

            payloadNode.style = styleAndClassName.style;
            payloadNode.icon = this.getNodeIcon( payloadNode.data.ciItem.id, item );
        }
    }

    /**
     *
     * @param condition
     * @param payloadItem
     */
    resolveCondition( condition : Item, payloadItem : Item ) : boolean {

        const conditionAttr  = dataExtraction.extractPropertiesOfItem( condition ),
              dataDescriptor = buildDataDescriptor( { attributePath: conditionAttr.leftExpression as string ?? 'item.' + payloadItem.itemType.displayAttribute }, {
                  id    : payloadItem.id + '',
                  origId: payloadItem.id,
                  item  : payloadItem
              }, true ),
              conditionData  = dataExtraction.extractConditionData( condition, this.metaContext, this.dataStructure, dataDescriptor, true );

        // We need props as upperCamelCase here
        const values = _.mapKeys( dataExtraction.extractPropertiesOfItem( payloadItem ), ( value, key ) => _.upperFirst( key ) );

        return resolveConditionRecursive( conditionData, values );

    }

    /**
     * Collect data covered by a DataObject on a given payload id path in tree
     * @param doId the id of the DataObject
     * @param payloadIdPath
     * @param type
     */
    getDataFromDataObject( doId : string, payloadIdPath : string, type : 'item' | 'reference' = 'item' ) : ObjectData | undefined {
        let retVal : ItemData[] | ReferenceData[] | undefined;

        const payloadIdPathParts = payloadIdPath.split( '-' );

        const possibleDataObjects = Object.keys( this.flatDataObjectNodeMap ).filter( doNodeId => ( doNodeId as string ).startsWith( doId ) ).map( doNodeId => this.flatDataObjectNodeMap![ doNodeId ] );

        const foundPayloadIds : number[] = [];
        while ( payloadIdPathParts.length ) {
            const currentPath = payloadIdPathParts.join( '-' );

            retVal = ( retVal ?? [] as any ).concat(
                possibleDataObjects
                    .filter( dataObj => ( dataObj.data.payloadIdPath === ( currentPath + '-' + dataObj.data.payloadId ) || dataObj.data.payloadIdPath?.startsWith( payloadIdPath ) ) && !foundPayloadIds.includes( dataObj.data.payloadId ) )
                    .map( dataObj => {
                        foundPayloadIds.push( dataObj.data.payloadId );
                        return {
                            type,
                            object: type === 'item'
                                ? global.data[ this.dataContext ]?.items[ dataObj.data.payloadId ]
                                : global.data[ this.dataContext ]?.references[ dataObj.data.payloadReferenceId ]
                        }
                    } )
            );
            payloadIdPathParts.pop();
        }

        return retVal;
    }

    /**
     * Resolve data from a given node and additional DataObjects (e.g. linked via isUsedBy ref of Actions)
     * @param node the appropriate node
     * @param dataObjects collection of additional DataObjects (linked via
     * @param resolvedObjectsDataContext
     * @param clonedObjectCache
     * @param clonedObjectsIdMappings
     */
    getResolvedDataObjects( node : ExtendTreeNode, dataObjects : DataObjects, resolvedObjectsDataContext : string = 'resolvedObjectsDataContext', triggerNode? : ExtendTreeNode, clonedObjectCache : Omit<ClonedObjectCache, 'originalStates'> = { items: {}, references: {} }, clonedObjectsIdMappings : ClonedObjectsIdMappings = { items: {}, references: {} } ) : ResolvedDataObjects {
        const retVal : ResolvedDataObjects = {},
              schema                       = this.payloadSchema,
              dataContext                  = this.dataContext,
              nodeItemId                   = node.data.payloadId ?? node.data.delegatedPayloadId,
              nodeParentItemId             = this.getParentPayloadIdForNode( node.id ),
              triggerNodeItemId            = triggerNode?.data.payloadId ?? triggerNode?.data.delegatedPayloadId,
              triggerNodeParentItemId      = triggerNode ? this.getParentPayloadIdForNode( triggerNode?.id ) : undefined,
              rootItemId                   = this.payloadRootItemId;

        if ( nodeItemId ) {
            try {
                const defaultObjects : DataObjects = {
                    // nodeObject (as fake DataObjects value)
                    nodeObject: {
                        id               : String( nodeItemId ),
                        origId           : nodeItemId,
                        item             : {
                            id  : nodeItemId,
                            key : 'nodeObject',
                            name: 'nodeObject'
                        },
                        dataSelectionMode: 'single'
                    },
                    // nodeParentObject (as fake DataObjects value)
                    nodeParentObject: {
                        id               : String( nodeParentItemId ),
                        origId           : nodeParentItemId ?? 0,
                        item             : {
                            id  : nodeParentItemId ?? 0,
                            key : 'nodeParentObject',
                            name: 'nodeParentObject'
                        },
                        dataSelectionMode: 'single'
                    },
                    // rootObject (as fake DataObjects value)
                    rootObject: {
                        id               : String( rootItemId ),
                        origId           : rootItemId ?? 0,
                        item             : {
                            id  : rootItemId ?? 0,
                            key : 'rootObject',
                            name: 'rootObject'
                        },
                        dataSelectionMode: 'single'
                    },
                    ...( triggerNode ? {
                        // nodeObject (as fake DataObjects value)
                        triggerObject: {
                            id               : String( triggerNodeItemId ),
                            origId           : triggerNodeItemId,
                            item             : {
                                id  : triggerNodeItemId,
                                key : 'triggerObject',
                                name: 'triggerObject'
                            },
                            dataSelectionMode: 'single'
                        },
                        // nodeParentObject (as fake DataObjects value)
                        triggerParentObject: {
                            id               : String( triggerNodeParentItemId ),
                            origId           : triggerNodeParentItemId ?? 0,
                            item             : {
                                id  : triggerNodeParentItemId ?? 0,
                                key : 'triggerParentObject',
                                name: 'triggerParentObject'
                            },
                            dataSelectionMode: 'single'
                        }
                    } : undefined )
                };
                const dataObjectsWithDefaultObjects = {
                    ...dataObjects,
                    ...defaultObjects
                }

                for ( const dataObjectLabel of Object.keys( dataObjectsWithDefaultObjects ) ) {
                    let items : Item[]           = [],
                        references : Reference[] = [];

                    // get special objects (fake DataObjects value)
                    if ( Object.keys( defaultObjects ).includes( dataObjectLabel ) ) {
                        const item = global.data[ dataContext ].items[ dataObjectsWithDefaultObjects[ dataObjectLabel ].origId ];

                        if ( item ) {
                            items.push( item );
                        }

                        retVal[ `${dataObjectLabel}Info` ] = {
                            dataContext       : resolvedObjectsDataContext,
                            itemType          : item?.itemType.name ?? '',
                            referenceSetId    : 0,
                            referenceDirection: 'SrcToDest',
                            otherItem         : undefined,
                            otherItemType     : '',
                            state             : {
                                itemId     : 0,
                                item       : '',
                                referenceId: 0,
                                reference  : ''
                            }
                        };
                    }
                    else {
                        const dataObject          = this.flatDataObjectNodeMap![ dataObjectsWithDefaultObjects[ dataObjectLabel ].id ],
                              itemObjectData      = this.getDataFromDataObject( dataObject.id, node.data.payloadIdPath ),
                              referenceObjectData = this.getDataFromDataObject( dataObject.id, node.data.payloadIdPath, 'reference' );

                        items = ( itemObjectData?.map( iOD => iOD.object ) ?? [] ) as Item[];
                        references = ( referenceObjectData?.map( rOD => rOD.object ) ?? [] ) as Reference[];
                    }

                    if ( items.length ) {
                        for ( const item of items ) {
                            const reference = references.find( ref => item.references.includes( ref.id ) );

                            // create an unmodified (to apply modifications only) item copy in action dataContext
                            if ( !clonedObjectCache?.items[ item.id ] ) {
                                const cloneItemData : ItemAttributes = item.toJSON();

                                if ( cloneItemData.id! < 1 ) {
                                    delete cloneItemData.id;
                                }

                                if ( cloneItemData.properties?.length ) {
                                    for ( const property of cloneItemData.properties ) {
                                        if ( property.id! < 1 ) {
                                            delete property.id;
                                        }
                                    }
                                }

                                if ( cloneItemData.enumProperties?.length ) {
                                    for ( const enumProperty of cloneItemData.enumProperties ) {
                                        if ( enumProperty.properties?.length ) {
                                            for ( const property of enumProperty.properties ) {
                                                if ( property.id! < 1 ) {
                                                    delete property.id;
                                                }
                                            }
                                        }
                                    }
                                }

                                clonedObjectCache.items[ item.id ] = new Item( cloneItemData, resolvedObjectsDataContext, schema );

                                if ( clonedObjectsIdMappings && item.id < 1 ) {
                                    clonedObjectsIdMappings.items[ clonedObjectCache.items[ item.id ].id ] = item.id;
                                }
                            }

                            // create an unmodified (to apply modifications only) reference copy in action dataContext
                            if ( reference && !clonedObjectCache.references[ reference.id ] ) {
                                const cloneReferenceData : ReferenceAttributes = reference.toJSON();

                                if ( cloneReferenceData.id! < 1 ) {
                                    delete cloneReferenceData.id;
                                }

                                if ( cloneReferenceData.properties?.length ) {
                                    for ( const property of cloneReferenceData.properties ) {
                                        if ( property.id! < 1 ) {
                                            delete property.id;
                                        }
                                    }
                                }

                                if ( cloneReferenceData.enumProperties?.length ) {
                                    for ( const enumProperty of cloneReferenceData.enumProperties ) {
                                        if ( enumProperty.properties?.length ) {
                                            for ( const property of enumProperty.properties ) {
                                                if ( property.id! < 1 ) {
                                                    delete property.id;
                                                }
                                            }
                                        }
                                    }
                                }

                                clonedObjectCache.references[ reference.id ] = new Reference( cloneReferenceData, resolvedObjectsDataContext, schema );

                                if ( reference.id < 1 ) {
                                    clonedObjectsIdMappings.references[ clonedObjectCache.references[ reference.id ].id ] = reference.id;
                                }
                            }

                            const resolvedDataObject : ResolvedDataObject = {
                                item    : clonedObjectCache.items[ item.id ],
                                property: new PropertyResolver( clonedObjectCache.items[ item.id ] )
                            };

                            retVal[ `${dataObjectLabel}Info` ] = retVal[ `${dataObjectLabel}Info` ] || {
                                dataContext       : resolvedObjectsDataContext,
                                itemType          : item?.itemType.name ?? '',
                                referenceSetId    : 0,
                                referenceDirection: 'SrcToDest',
                                otherItem         : undefined,
                                otherItemType     : '',
                                state             : []
                            };

                            if ( dataObjectsWithDefaultObjects[ dataObjectLabel ].dataSelectionMode === 'single' ) {
                                ( retVal[ `${dataObjectLabel}Info` ].state as ResolvedDataObjectState ) = {
                                    itemId     : item.id,
                                    item       : JSON.stringify( clonedObjectCache.items[ item.id ].toUpdateJSON( 'modified' ) ),
                                    referenceId: 0,
                                    reference  : ''
                                };
                            }
                            else {
                                ( retVal[ `${dataObjectLabel}Info` ].state as ResolvedDataObjectState[] ).push( {
                                    itemId     : item.id,
                                    item       : JSON.stringify( clonedObjectCache.items[ item.id ].toUpdateJSON( 'modified' ) ),
                                    referenceId: 0,
                                    reference  : ''
                                } )
                            }

                            if ( reference ) {
                                resolvedDataObject.reference = clonedObjectCache.references[ reference.id ];
                                resolvedDataObject.referenceProperty = new ReferencePropertyResolver( clonedObjectCache.references[ reference.id ] );

                                if ( dataObjectsWithDefaultObjects[ dataObjectLabel ].dataSelectionMode === 'single' ) {
                                    ( retVal[ `${dataObjectLabel}Info` ].state as ResolvedDataObjectState ).reference = JSON.stringify( clonedObjectCache.references[ reference.id ].toUpdateJSON( 'modified' ) );
                                }
                                else {
                                    ( retVal[ `${dataObjectLabel}Info` ].state as ResolvedDataObjectState[] )[ ( retVal[ `${dataObjectLabel}Info` ].state as ResolvedDataObjectState[] ).length - 1 ].reference = JSON.stringify( clonedObjectCache.references[ reference.id ].toUpdateJSON( 'modified' ) )
                                }
                            }

                            if ( dataObjectsWithDefaultObjects[ dataObjectLabel ].dataSelectionMode === 'single' ) {
                                retVal[ dataObjectLabel ] = resolvedDataObject;

                                break;
                            }
                            else {
                                if ( !retVal[ dataObjectLabel ] ) {
                                    retVal[ dataObjectLabel ] = [];
                                }

                                ( retVal[ dataObjectLabel ] as ResolvedDataObject[] ).push( resolvedDataObject );
                            }
                        }
                    }
                    else if ( dataObjectsWithDefaultObjects[ dataObjectLabel ].dataSelectionMode === 'single' ) {
                        retVal[ dataObjectLabel ] = {} as ResolvedDataObject;
                    }
                    else if ( dataObjectsWithDefaultObjects[ dataObjectLabel ].dataSelectionMode === 'all' ) {
                        retVal[ dataObjectLabel ] = [];
                    }
                }
            }
            catch ( e ) {
                console.error( e );
            }
        }

        return retVal;
    }


    getNodeActions( node : ExtendTreeNode ) : Action[] {
        const nodeItem = global.data[ this.metaContext ]?.items[ node.data.itemId ];

        let retVal : Action[] = [];

        if ( nodeItem ) {
            retVal = dataUtils.findReferences(
                this.metaContext, this.isTriggerForCirsIds, node.data.itemId, undefined, true
            )?.map( ref => dataExtraction.extractActionData( ref.destItem!, this.metaContext, this.dataStructure ) )
                .sort( ( action1, action2 ) => ( action1.order ?? 0 ) - ( action2.order ?? 0 ) ) ?? [];
        }
        return retVal;
    }

    /**
     *
     * @param nodeId
     * @param payloadItem
     */
    getNodeStyleAndClassName( nodeId : number, payloadItem? : Item ) : { style : CSSProperties, className : string } {
        let retVal : { style : CSSProperties, className : string } = { style: {}, className: '' };

        const cacheId = nodeId + ( payloadItem ? '-' + payloadItem.id : '' );

        // use cached style if non-conditional
        if ( this.nodeStyleCache[ cacheId ] ) {
            retVal = this.nodeStyleCache[ cacheId ];
        }
        else {
            const nodeItem = global.data[ this.metaContext ]?.items[ nodeId ];

            let isConditional : boolean = false;

            if ( nodeItem ) {

                const styleItems = dataUtils.findReferences(
                    this.metaContext, this.isStyleForCirsIds, undefined, nodeId, true
                )?.map( ref => dataExtraction.extractPropertiesOfItem( ref.srcItem! ) as Style & { id : number } )
                    .sort( ( style1, style2 ) => ( style1.order ?? 0 ) - ( style2.order ?? 0 ) );

                if ( styleItems?.length ) {

                    for ( const style of styleItems ) {
                        const condition = dataUtils.findReferences(
                            this.metaContext, this.isApplyingCirsIds, undefined, style.id, true
                        )?.map( ref => ref.srcItem! )?.[ 0 ];

                        let conditionResult = false;
                        if ( condition && payloadItem ) {
                            conditionResult = this.resolveCondition( condition, payloadItem );
                            isConditional = true;
                        }
                        if ( !isConditional || conditionResult ) {
                            if ( style.className ) {
                                retVal.className += ' ' + Object.keys( style.className ).join( ' ' );
                            }

                            if ( style.style ) {
                                const styleToApply : { [ key : string ] : string } = {};

                                for ( const key of Object.keys( style.style ) as string[] ) {
                                    styleToApply[ _.camelCase( key ) ] = style.style[ key ];
                                }

                                retVal.style = { ...retVal.style, ...styleToApply };
                            }
                        }

                    }

                }

            }

            // cache style if non-conditional
            if ( !isConditional ) {
                this.nodeStyleCache[ cacheId ] = retVal;
            }

        }

        return retVal;
    }

    getNodeIcon( nodeId : number, payloadItem? : Item ) : string | ( () => React.ReactNode ) | undefined {
        let retVal : string | ( () => React.ReactNode ) | undefined;

        const cacheId = nodeId + ( payloadItem ? '-' + payloadItem.id : '' );

        // use cached icon if non-conditional
        if ( this.nodeIconCache[ cacheId ] ) {
            retVal = this.nodeIconCache[ cacheId ];
        }
        else {
            const nodeItem = global.data[ this.metaContext ]?.items[ nodeId ];

            let isConditional : boolean = false;

            if ( nodeItem ) {

                // use iconClass property as fallback (decided by FS on 2023-05-25)
                retVal = dataExtraction.extractPropertiesOfItem( nodeItem ).iconClass;

                const imageItems = dataUtils.findReferences(
                    this.metaContext, this.isIconForCirsIds, undefined, nodeId, true
                )?.map( ref => dataExtraction.extractPropertiesOfItem( ref.srcItem! ) )
                    .sort( ( image1, image2 ) => ( image2.order ?? 0 ) - ( image1.order ?? 0 ) );

                if ( imageItems?.length ) {

                    for ( const image of imageItems ) {
                        const condition = dataUtils.findReferences(
                            this.metaContext, this.isApplyingCirsIds, undefined, image.id, true
                        )?.map( ref => ref.srcItem! )?.[ 0 ];
                        let conditionResult      = false,
                            currentIsConditional = false;
                        if ( condition && payloadItem ) {
                            conditionResult = this.resolveCondition( condition, payloadItem );
                            isConditional = true;
                            currentIsConditional = true;
                        }
                        if ( image && ( !currentIsConditional || conditionResult ) ) {
                            retVal = image.cssClass || getIconCssClassName( 'byRef', image.key );
                        }
                    }

                }

                if ( retVal ) {
                    retVal = 'yn-tree-icon ' + retVal;
                }

                // if no explicit icon is set or the condition doesn't apply, use the standard icon
                if ( payloadItem && !retVal ) {
                    retVal = 'yn-tree-icon icon-itemtype-detail ' + payloadItem.itemType.icon;
                }

            }

            if ( retVal ) {
                retVal = resolveLigatureIcon( retVal as string );
            }

            // cache icon if non-conditional
            if ( !isConditional ) {
                this.nodeIconCache[ cacheId ] = retVal;
            }

        }

        return retVal;
    }

    /**
     * Compares two payloadNodes for sorting
     * @param {String} predicate
     * @param {ExtendTreeNode} payloadNode1
     * @param {ExtendTreeNode} payloadNode2
     * @return {0|1|-1}
     */
    payloadNodeSorter( predicate : string, payloadNode1 : ExtendTreeNode, payloadNode2 : ExtendTreeNode ) : 0 | 1 | -1 {
        let retVal : 0 | 1 | -1 = 0;

        if ( payloadNode1.data.payloadId && payloadNode2.data.payloadId ) {

            predicate = predicate.replace( /(?:item\.([a-zA-Z]*))/, ( m, m1 ) => 'item.' + _.camelCase( m1 ) );

            for ( const predicatePart of predicate.split( ',' ) ) {
                const parts = predicatePart.match( /^\s*((\w+)\.(\w+)((?:\.\w+)*))\s*(asc|desc)?\s*$/i );
                if ( parts ) {
                    const descending = ( parts[ 5 ] ?? '' ).toLowerCase() === 'desc';
                    let propertyTypeName : string = '',
                        dataDescriptorParams : ObjectDataDescriptorParams;
                    if ( parts[ 2 ] === 'property' ) {
                        propertyTypeName = parts[ 3 ];
                        dataDescriptorParams = {
                            type            : 'property',
                            propertyTypeName: parts[ 3 ],
                            attribute       : ( parts[ 4 ] || 'value' ) as PropertyDataDescriptor[ 'attribute' ]
                        };
                        //getDataDescriptorParams( parts[ 2 ] + ( parts[ 4 ] || '.value' ) );
                    }
                    else {
                        dataDescriptorParams = {
                            type     : 'item',
                            attribute: ( parts[ 3 ] ?? 'key' ) as ItemDataDescriptor[ 'attribute' ]
                        };
                        //getDataDescriptorParams( parts[ 1 ] );
                    }
                    if ( dataDescriptorParams ) {
                        const
                            // get items from global data
                            leftItem        = global.data[ this.dataContext ]?.items[ payloadNode1.data.payloadId ],
                            rightItem       = global.data[ this.dataContext ]?.items[ payloadNode2.data.payloadId ],
                            // build data objects
                            leftDataObject  = dataDescriptorParams.type === 'item' ? {
                                type  : dataDescriptorParams.type,
                                object: leftItem
                            } as ItemData : {
                                type  : dataDescriptorParams.type,
                                object: leftItem.properties.get( metaModel[ this.payloadSchema ]?.getPropertyType( propertyTypeName )?.id ?? 0 )
                            } as PropertyData,
                            rightDataObject = dataDescriptorParams.type === 'item' ? {
                                type  : dataDescriptorParams.type,
                                object: rightItem
                            } as ItemData : {
                                type  : dataDescriptorParams.type,
                                object: rightItem.properties.get( metaModel[ this.payloadSchema ]?.getPropertyType( propertyTypeName )?.id ?? 0 )
                            } as PropertyData;

                        let
                            // get values
                            leftValue  = leftDataObject?.object
                                ? dataUtils.getValueFromData( dataDescriptorParams, leftDataObject )
                                // get default value for properties if not set
                                : ( dataDescriptorParams.type === 'property' && dataDescriptorParams.attribute === 'value'
                                        ? metaModel[ this.payloadSchema ]?.getPropertySetForItemTypeAndPropertyType( leftItem.itemTypeId, propertyTypeName )?.valueDefault ?? 0
                                        : 0
                                ),
                            rightValue = rightDataObject?.object
                                ? dataUtils.getValueFromData( dataDescriptorParams, rightDataObject )
                                // get default value for properties if not set
                                : ( dataDescriptorParams.type === 'property' && dataDescriptorParams.attribute === 'value'
                                        ? metaModel[ this.payloadSchema ]?.getPropertySetForItemTypeAndPropertyType( rightItem.itemTypeId, propertyTypeName )?.valueDefault ?? 0
                                        : 0
                                );

                        // convert string values to lower case to sort case-insensitive
                        if ( typeof leftValue === 'string' ) {
                            leftValue = leftValue.toLowerCase();
                        }
                        if ( typeof rightValue === 'string' ) {
                            rightValue = rightValue.toLowerCase();
                        }

                        // compare
                        retVal = ( leftValue ?? 0 ) < ( rightValue ?? 0 ) ? ( descending ? 1 : -1 ) : ( ( rightValue ?? 0 ) < ( leftValue ?? 0 ) ? ( descending ? -1 : 1 ) : 0 );

                        if ( retVal ) {
                            break;
                        }
                    }

                }
            }
        }

        return retVal;

    }

    sortPayloadNodes( payloadNodes : ExtendTreeNode[], props : ExtractedProperties ) {
        let orderBy = props.orderBy ?? 'item.key';
        if ( !orderBy.includes( 'item.key' ) ) {
            orderBy += ',item.key';
        }
        payloadNodes.sort( this.payloadNodeSorter.bind( this, orderBy ) );
    }

    /**
     * Applies payload data to leftExpression of condition. Used for filterPayloadNodes
     * @param condition
     * @param payloadNode
     */
    applyNodeDataToConditionRecursively( condition : Condition, payloadNode : ExtendTreeNode ) : { [ key : string ] : any } {
        let retVal : { [ key : string ] : any } = {};
        if ( _.isArray( condition.leftExpression ) ) {
            for ( const condi of condition.leftExpression ) {
                retVal = { ...retVal, ...this.applyNodeDataToConditionRecursively( condi, payloadNode ) };
            }
        }
        else {
            for ( const side of [ 'left', 'right' ] ) {
                const sideExpression               = side === 'left'
                          ? 'leftExpression'
                          : 'rightExpression',
                      sideExpressionDataDescriptor = side === 'left'
                          ? 'leftExpressionDataDescriptor'
                          : 'rightExpressionDataDescriptor',
                      sideReferenceSetId           = side === 'left'
                          ? this.providesDataForLeftExpressionReferenceSetId
                          : this.providesDataForRightExpressionReferenceSetId,
                      providesDataForReference     = dataUtils.findReferences(
                          this.metaContext,
                          sideReferenceSetId,
                          undefined,
                          condition.id!
                      )?.[ 0 ];

                let relevantDataObjectId : string = payloadNode.siblingId! as string;

                if ( providesDataForReference ) {
                    relevantDataObjectId = 'do_' + providesDataForReference.srcItemId;
                }

                // if condition has no providesDataFor(Left|Right)Expression reference to a dataObject use payloads dataObject as default
                if ( !condition[ sideExpressionDataDescriptor ] ) {
                    const dataObject = getDataObject( relevantDataObjectId, this.dataStructure )
                        ?? {
                            id    : payloadNode.data.itemId,
                            origId: payloadNode.data.itemId,
                            item  : payloadNode.data.ciItem
                        };

                    condition[ sideExpressionDataDescriptor ] = buildDataDescriptor(
                        { attributePath: condition[ sideExpression ] as string },
                        dataObject
                    );

                    if ( condition[ sideExpressionDataDescriptor ] ) {
                        delete condition[ sideExpression ];
                    }
                }

                if ( condition[ sideExpressionDataDescriptor ] ) {
                    const data = dataUtils.getData( condition[ sideExpressionDataDescriptor ], this.dataStructure, this.dataContext, this.payloadRootItemId );

                    if ( data?.length ) {
                        let filteredData = data[ 0 ];

                        if ( data.length > 1 ) {
                            switch ( condition[ sideExpressionDataDescriptor ].type ) {
                                case 'item':
                                    filteredData = data.find( d => d.object?.id === payloadNode.data.payloadId );
                                    break;
                                case 'property':
                                    filteredData = data.find( d => d.object?.itemId === payloadNode.data.payloadId );
                                    break;
                                case 'reference':
                                    filteredData = data.find( d => d.object?.id === payloadNode.data.payloadReferenceId );
                                    break;
                                case 'referenceProperty':
                                    filteredData = data.find( d => d.object?.referenceId === payloadNode.data.payloadReferenceId );
                                    break;
                            }
                        }

                        if ( filteredData ) {
                            const key   = condition[ sideExpressionDataDescriptor ]!.alias,
                                  value = dataUtils.getValueFromData( condition[ sideExpressionDataDescriptor ], filteredData );

                            retVal[ key ] = value;
                        }
                    }
                }
            }
        }

        return retVal;
    }

    /**
     * Filter payload by condition
     * @param payloadNodes
     * @param props
     */
    filterPayloadNodes( payloadNodes : ExtendTreeNode[], props : ExtractedProperties ) {
        const retVal : ExtendTreeNode[] = payloadNodes,
              isFilterForReference      = dataUtils.findReferences( this.metaContext, this.conditionIsFilterForDynamicNodeCirsId, undefined, props.id )?.[ 0 ];

        if ( isFilterForReference ) {
            const conditionItem = global.data[ this.metaContext ]?.items[ isFilterForReference.srcItemId ];

            if ( conditionItem ) {
                const condition = dataExtraction.extractConditionData( conditionItem, this.metaContext, this.dataStructure );

                payloadNodes.forEach( payloadNode => {
                    const currentCondition = omniclone( condition ),
                          conditionData    = {
                              ...this.applyNodeDataToConditionRecursively( currentCondition, payloadNode )
                          };

                    if ( !resolveConditionRecursive( currentCondition, conditionData ) ) {
                        payloadNode.visibility = 'hidden';
                    }
                } );
            }
        }

        return retVal;
    }
}
